import { useState, useCallback, useMemo, useRef, Fragment, useEffect, useImperativeHandle, forwardRef } from "react";
import { useSearchParams } from "react-router-dom";
import useTabDeepLink from '../hooks/useTabDeepLink';
import SolmanMonthlyReport from "./SolmanMonthlyReport";
import SolmanStatusAging from "./SolmanStatusAging";
import { Ticket, Clock, CheckCircle, XCircle, Percent } from "lucide-react";
import * as XLSX from "xlsx-js-style";
  // ← Auth context for role checking
import { BarChart, Bar, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from "recharts";
import { useAuth } from '../context/AuthContext';  // ← Auth context
import { useLang } from '../context/LanguageContext';
import ImpactTeamMap from './ImpactTeamMap';
  // ← Auth context

/* ── Design tokens ─────────────────────────────── */
const T = {
  sidebar:"#0F172A", sidebarHover:"#1E293B", sidebarActive:"#1E3A5F",
  sidebarAccent:"#38BDF8", sidebarText:"#94A3B8", sidebarTextActive:"#F1F5F9",
  bg:"#F8FAFC", card:"#FFFFFF", border:"#E2E8F0", borderHover:"#CBD5E1",
  text:"#0F172A", textSecondary:"#64748B", textTertiary:"#94A3B8",
  blue:"#2563EB", blueBg:"#EFF6FF",
  green:"#16A34A", greenBg:"#F0FDF4",
  amber:"#D97706", amberBg:"#FFFBEB",
  red:"#DC2626",   redBg:"#FEF2F2",
  purple:"#7C3AED",purpleBg:"#F5F3FF",
  teal:"#0D9488",  tealBg:"#F0FDFA",
};

const STATUS_COLORS = {
  "Closed":                    {dot:"#16A34A",bg:"#F0FDF4",chart:"#16A34A"},
  "Solution Proposed to User": {dot:"#2563EB",bg:"#EFF6FF",chart:"#2563EB"},
  "Feedback Awaited - User":   {dot:"#D97706",bg:"#FFFBEB",chart:"#D97706"},
  "Agreed by User":            {dot:"#0D9488",bg:"#F0FDFA",chart:"#0D9488"},
  "In process - Support Team": {dot:"#7C3AED",bg:"#F5F3FF",chart:"#7C3AED"},
  "Approval Awaited":          {dot:"#EA580C",bg:"#FFF7ED",chart:"#EA580C"},
  "Withdrawn":                 {dot:"#6B7280",bg:"#F9FAFB",chart:"#6B7280"},
};
const STATUS_LIST   = Object.keys(STATUS_COLORS);
const STATUS_SHORT  = s => s.replace("Solution Proposed to User","Proposed").replace("Feedback Awaited - User","Feedback Awaited").replace("In process - Support Team","In Process");

const PRIORITY_CFG = {
  "1: Very High":{color:"#DC2626",bg:"#FEF2F2",label:"Very High"},
  "2: High":     {color:"#D97706",bg:"#FFFBEB",label:"High"},
  "3: Medium":   {color:"#2563EB",bg:"#EFF6FF",label:"Medium"},
  "4: Low":      {color:"#16A34A",bg:"#F0FDF4",label:"Low"},
};

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTH_FULL  = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH_NUM  = ["01","02","03","04","05","06","07","08","09","10","11","12"];
const COMPANY_PALETTE = ["#2563EB","#7C3AED","#0D9488","#D97706","#DC2626","#EA580C","#6B7280"];

/* ── Export ke Excel — kolom & tipe HARUS persis sama dgn file contoh admin ──
   (dicek langsung per-sel dari file contoh: ID/Client/SAP Installation/
   Software Release = angka, Created On2 = tanggal asli, sisanya teks —
   termasuk Transaction Type ID & Status ID yg SEKILAS mirip angka/kode tapi
   sebenarnya TEKS di file aslinya, mis. "ZMIN"/"E0005"). */
const EXPORT_HEADERS = [
  "ID","Created By","Company","Description","Priority","Created On","Created On2","Status",
  "Reporter","Impact","Processor","Service Team","System ID","System Status","Transaction Type",
  "Client","Transaction Type ID","Status ID","Trans. Category","System","SAP Installation",
  "Software Release","SAP Component",
];
const EXPORT_NUMERIC_COLS = new Set(["ID","Client","SAP Installation","Software Release"]);
const EXPORT_COL_WIDTHS = {
  "ID":11,"Created By":15.3,"Company":9.6,"Description":28.1,"Priority":11.1,"Created On":11.3,
  "Created On2":12.3,"Status":24.1,"Reporter":34.9,"Impact":13.6,"Processor":28.9,
  "Service Team":24,"System ID":10.1,"System Status":33.4,"Transaction Type":16.3,"Client":6.6,
  "Transaction Type ID":18.9,"Status ID":10,"Trans. Category":15.1,"System":18.7,
  "SAP Installation":15.1,"Software Release":16.9,"SAP Component":15.7,
};
// Parse "Created On2" fleksibel: bisa berupa Date (dari embed/export lama), teks
// dd.mm.yyyy (spt "Created On"), atau ISO string — kembalikan Date asli utk Excel.
function parseAnyDate(v){
  if(!v) return null;
  if(v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const s = String(v).trim();
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if(m) return new Date(Number(m[3]), Number(m[2])-1, Number(m[1]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

const NAV = [
  {id:"overview",labelKey:"solm.nav.overview",icon:"ti-layout-dashboard"},
  {id:"trends",  labelKey:"solm.nav.trends",  icon:"ti-chart-line"},
  {id:"teams",   labelKey:"solm.nav.teams",   icon:"ti-users"},
  {id:"detail",  labelKey:"solm.nav.detail",  icon:"ti-list-details"},
  {id:"pivot",   labelKey:"solm.nav.pivot",   icon:"ti-table"},
  {id:"aging",   labelKey:"solm.nav.aging",   icon:"ti-hourglass"},
  {id:"report",  labelKey:"solm.nav.report",  icon:"ti-mail-forward"},
];
const OPEN_STATUSES = ["New","In process - Support Team","In Process - User","Feedback Awaited - User","Feedback Awaited - Supp. Team","Solution Proposed to User","Sent to SAP","Approval Awaited","Approved","Agreed by User"];

/* ── Parse ─────────────────────────────────────── */
function parseDate(s){if(!s)return null;const p=s.split(".");return p.length===3?new Date(`${p[2]}-${p[1]}-${p[0]}`):null;}

/* Hydrate pre-parsed rows (from export) — restore _date from _monthKey */
function hydrateRows(rows){
  return rows.map(r=>{
    if(r._monthKey&&!r._date){
      const[y,m]=r._monthKey.split("-").map(Number);
      r._date=new Date(y,m-1,1);
      r._year=y;
      r._month=m-1;
    }
    return r;
  });
}

function countBy(arr,key){return arr.reduce((a,r)=>{const k=r[key]||"Unknown";a[k]=(a[k]||0)+1;return a;},{});}

/* ── Reusable: Status badge ─────────────────────── */
function StatusBadge({status,count}){
  const cfg=STATUS_COLORS[status]||{dot:"#94A3B8",bg:"#F9FAFB"};
  return(
    <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",minWidth:32,padding:"2px 9px",borderRadius:99,background:cfg.bg,color:cfg.dot,fontSize:12,fontWeight:700}}>
      {count}
    </span>
  );
}

/* ── Reusable: Month × Status table ────────────── */
function MonthStatusTable({data, title, subtitle, filtered, openTickets}){
  const { t } = useLang();
  const clickable=!!(filtered&&openTickets);
  // data: array of {label, date, ...STATUS_LIST keys, total}
  if(!data||!data.length)return null;
  const statuses=STATUS_LIST.filter(s=>data.some(r=>r[s]>0));
  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"20px 24px"}}>
      {title&&<div style={{marginBottom:16}}>
        <h3 style={{fontSize:14,fontWeight:700,color:T.text,margin:0}}>{title}</h3>
        {subtitle&&<p style={{fontSize:12,color:T.textTertiary,margin:"3px 0 0"}}>{subtitle}</p>}
      </div>}
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{background:T.bg}}>
              <th style={{padding:"9px 14px",textAlign:"left",fontWeight:700,color:T.textSecondary,borderBottom:`1px solid ${T.border}`,fontSize:11,textTransform:"uppercase",letterSpacing:"0.05em",position:"sticky",left:0,background:T.bg,zIndex:2,whiteSpace:"nowrap",minWidth:100}}>{t('solm.month')}</th>
              {statuses.map(s=>(
                <th key={s} style={{padding:"9px 10px",textAlign:"center",fontWeight:600,color:STATUS_COLORS[s].dot,borderBottom:`1px solid ${T.border}`,whiteSpace:"nowrap",fontSize:11,minWidth:72}}>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                    <span style={{width:7,height:7,borderRadius:"50%",background:STATUS_COLORS[s].dot}}/>
                    {STATUS_SHORT(s)}
                  </div>
                </th>
              ))}
              <th style={{padding:"9px 14px",textAlign:"center",fontWeight:800,color:T.blue,borderBottom:`1px solid ${T.border}`,background:T.blueBg,fontSize:11,textTransform:"uppercase"}}>{t('solm.total')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row,i)=>(
              <tr key={row.label} style={{borderBottom:`1px solid ${T.border}`,background:i%2===0?T.card:T.bg}}>
                <td style={{padding:"8px 14px",fontWeight:600,color:T.text,position:"sticky",left:0,background:i%2===0?T.card:T.bg,zIndex:1,whiteSpace:"nowrap",fontSize:13}}>{row.label}</td>
                {statuses.map(s=>(
                  <td key={s} onClick={(clickable&&row[s])?()=>openTickets(row.label,s,filtered.filter(r=>r._monthKey===row.key&&r.Status===s)):undefined} style={{padding:"8px 10px",textAlign:"center",borderLeft:`1px solid ${T.border}`,cursor:(clickable&&row[s])?"pointer":"default"}}>
                    {row[s]?<StatusBadge status={s} count={row[s]}/>:<span style={{color:T.border}}>—</span>}
                  </td>
                ))}
                <td onClick={(clickable&&row.total)?()=>openTickets(row.label,"All statuses",filtered.filter(r=>r._monthKey===row.key)):undefined} style={{padding:"8px 14px",textAlign:"center",fontWeight:800,color:T.blue,background:T.blueBg,fontSize:13,cursor:(clickable&&row.total)?"pointer":"default"}}>{row.total||0}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{background:T.bg,borderTop:`2px solid ${T.border}`}}>
              <td style={{padding:"10px 14px",fontWeight:800,color:T.text,position:"sticky",left:0,background:T.bg,fontSize:13}}>{t('solm.total')}</td>
              {statuses.map(s=>(
                <td key={s} style={{padding:"10px",textAlign:"center",borderLeft:`1px solid ${T.border}`,fontWeight:700,color:STATUS_COLORS[s].dot}}>
                  {data.reduce((a,r)=>a+(r[s]||0),0)||"—"}
                </td>
              ))}
              <td style={{padding:"10px 14px",textAlign:"center",fontWeight:900,color:T.blue,background:T.blueBg,fontSize:14}}>
                {data.reduce((a,r)=>a+(r.total||0),0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── Reusable: Monthly simple table (closed/open/rate) ── */
function MonthlyBreakdown({filtered}){
  const { t } = useLang();
  const data=useMemo(()=>{
    const map={};
    filtered.forEach(r=>{
      if(!r._monthKey)return;
      if(!map[r._monthKey])map[r._monthKey]={key:r._monthKey,label:r._monthLabel,date:r._date,total:0,closed:0,open:0};
      map[r._monthKey].total++;
      if(r.Status==="Closed")map[r._monthKey].closed++;
      if(OPEN_STATUSES.includes(r.Status))map[r._monthKey].open++;
    });
    return Object.values(map).sort((a,b)=>a.date-b.date);
  },[filtered]);
  if(!data.length)return null;
  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"20px 24px"}}>
      <div style={{marginBottom:16}}>
        <h3 style={{fontSize:14,fontWeight:700,color:T.text,margin:0}}>{t('solm.monthlySummary')}</h3>
        <p style={{fontSize:12,color:T.textTertiary,margin:"3px 0 0"}}>{t('solm.monthlySummarySub')}</p>
      </div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead>
            <tr style={{background:T.bg}}>
              {["Month","Total","Closed","Open","Close Rate","Progress"].map(h=>(
                <th key={h} style={{padding:"9px 14px",textAlign:h==="Month"?"left":"center",fontWeight:700,color:T.textSecondary,borderBottom:`1px solid ${T.border}`,fontSize:11,textTransform:"uppercase",letterSpacing:"0.05em",whiteSpace:"nowrap",...(h==="Progress"?{minWidth:160}:{})}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row,i)=>{
              const rate=row.total?Math.round((row.closed/row.total)*100):0;
              return(
                <tr key={row.key} style={{background:i%2===0?T.card:T.bg}}>
                  <td style={{padding:"9px 14px",fontWeight:600,color:T.text,borderBottom:`1px solid ${T.border}`,whiteSpace:"nowrap"}}>{row.label}</td>
                  <td style={{padding:"9px 14px",textAlign:"center",fontWeight:700,color:T.blue,borderBottom:`1px solid ${T.border}`}}>{row.total}</td>
                  <td style={{padding:"9px 14px",textAlign:"center",borderBottom:`1px solid ${T.border}`}}><span style={{display:"inline-block",padding:"2px 9px",borderRadius:99,background:T.greenBg,color:T.green,fontSize:12,fontWeight:600}}>{row.closed}</span></td>
                  <td style={{padding:"9px 14px",textAlign:"center",borderBottom:`1px solid ${T.border}`}}><span style={{display:"inline-block",padding:"2px 9px",borderRadius:99,background:row.open>0?T.amberBg:T.bg,color:row.open>0?T.amber:T.textTertiary,fontSize:12,fontWeight:600}}>{row.open}</span></td>
                  <td style={{padding:"9px 14px",textAlign:"center",fontWeight:700,color:rate>=80?T.green:rate>=50?T.amber:T.red,borderBottom:`1px solid ${T.border}`}}>{rate}%</td>
                  <td style={{padding:"9px 14px",borderBottom:`1px solid ${T.border}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{flex:1,height:6,borderRadius:99,background:T.bg,border:`1px solid ${T.border}`,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${rate}%`,background:rate>=80?T.green:rate>=50?T.amber:T.red,borderRadius:99}}/>
                      </div>
                      <span style={{fontSize:11,color:T.textTertiary,minWidth:30}}>{rate}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{background:T.bg,borderTop:`2px solid ${T.border}`}}>
              <td style={{padding:"10px 14px",fontWeight:800,color:T.text,fontSize:13}}>{t('solm.total')}</td>
              <td style={{padding:"10px 14px",textAlign:"center",fontWeight:800,color:T.blue}}>{data.reduce((s,r)=>s+r.total,0)}</td>
              <td style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:T.green}}>{data.reduce((s,r)=>s+r.closed,0)}</td>
              <td style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:T.amber}}>{data.reduce((s,r)=>s+r.open,0)}</td>
              <td colSpan={2} style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:T.textSecondary}}>
                {(()=>{const t=data.reduce((s,r)=>s+r.total,0),c=data.reduce((s,r)=>s+r.closed,0);return t?`${Math.round(c/t*100)}%`:"—";})()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── Sidebar ───────────────────────────────────── */
function KpiCard({label,value,icon,color,bg,sub,onClick}){
  return(
    <div onClick={onClick} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px",display:"flex",flexDirection:"column",gap:12,cursor:onClick?"pointer":"default",transition:"box-shadow 0.15s,transform 0.15s"}} onMouseEnter={onClick?e=>{e.currentTarget.style.boxShadow="0 4px 14px rgba(0,0,0,0.10)";e.currentTarget.style.transform="translateY(-1px)";}:undefined} onMouseLeave={onClick?e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.transform="none";}:undefined}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <span style={{fontSize:13,color:T.textSecondary,fontWeight:500}}>{label}</span>
        <div style={{width:36,height:36,borderRadius:10,background:bg,display:"flex",alignItems:"center",justifyContent:"center",border:`1px solid ${color}22`}}>
          {icon&&(()=>{const Icon=icon;return<Icon size={18} color={color} strokeWidth={2}/>;})()}
        </div>
      </div>
      <div>
        <div style={{fontSize:30,fontWeight:700,color:T.text,letterSpacing:"-1px",lineHeight:1}}>{typeof value==="number"?value.toLocaleString():value}</div>
        {sub&&<div style={{fontSize:12,color:T.textTertiary,marginTop:5}}>{sub}</div>}
      </div>
    </div>
  );
}

/* ── Card wrapper ──────────────────────────────── */
function Card({title,subtitle,children,style:s}){
  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"20px 24px",...s}}>
      {title&&<div style={{marginBottom:18}}>
        <h3 style={{fontSize:14,fontWeight:700,color:T.text,margin:0,letterSpacing:"-0.2px"}}>{title}</h3>
        {subtitle&&<p style={{fontSize:12,color:T.textTertiary,margin:"3px 0 0"}}>{subtitle}</p>}
      </div>}
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════
   TICKET DRAWER — slide-in detail panel
══════════════════════════════════════════════ */
const PRIORITY_COLOR={
  "1: Very High":{color:"#DC2626",bg:"#FEF2F2"},
  "2: High":     {color:"#D97706",bg:"#FFFBEB"},
  "3: Medium":   {color:"#2563EB",bg:"#EFF6FF"},
  "4: Low":      {color:"#16A34A",bg:"#F0FDF4"},
};
function TicketDrawer({sel, onClose}){
  const { t } = useLang();
  const [openIdx,setOpenIdx]=useState(null);
  useEffect(()=>{ setOpenIdx(null); },[sel]);
  if(!sel)return null;
  const {team,status,monthLabel,tickets}=sel;
  const cfg=STATUS_COLORS[status]||{dot:"#94A3B8",bg:"#F9FAFB"};

  return(
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.45)",zIndex:1000,backdropFilter:"blur(2px)"}}/>
      {/* Drawer */}
      <div style={{position:"fixed",top:0,right:0,height:"100vh",width:"min(780px,92vw)",background:T.card,zIndex:1001,display:"flex",flexDirection:"column",boxShadow:"-8px 0 40px rgba(0,0,0,0.18)"}}>

        {/* Drawer header */}
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,background:"#0F172A",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <span style={{fontSize:15,fontWeight:700,color:"#F1F5F9"}}>{team}</span>
              <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:99,background:cfg.bg,color:cfg.dot,fontSize:12,fontWeight:700}}>
                <span style={{width:7,height:7,borderRadius:"50%",background:cfg.dot}}/>
                {STATUS_SHORT(status)}
              </span>
              {monthLabel&&<span style={{fontSize:12,color:"#94A3B8",padding:"2px 8px",border:"1px solid #334155",borderRadius:6}}>{monthLabel}</span>}
            </div>
            <div style={{fontSize:12,color:"#64748B"}}>{tickets.length} ticket{tickets.length!==1?"s":""} found</div>
          </div>
          <button onClick={onClose} style={{width:32,height:32,borderRadius:8,background:"#1E293B",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#94A3B8"}}>
            <i className="ti ti-x" style={{fontSize:16}}/>
          </button>
        </div>

        {/* Table */}
        <div style={{flex:1,overflowY:"auto",overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead style={{position:"sticky",top:0,zIndex:2}}>
              <tr style={{background:"#F1F5F9"}}>
                {[
                  {label:"ID",        w:110},
                  {label:"Created On",w:100},
                  {label:"Company",   w:70},
                  {label:"Priority",  w:100},
                  {label:"Status",    w:160},
                  {label:"Created By",w:130},
                  {label:"Description",w:300},
                ].map(col=>(
                  <th key={col.label} style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:T.textSecondary,borderBottom:`1px solid ${T.border}`,fontSize:11,textTransform:"uppercase",letterSpacing:"0.05em",whiteSpace:"nowrap",minWidth:col.w}}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tickets.map((r,i)=>{
                const pcfg=PRIORITY_COLOR[r.Priority]||{color:T.textSecondary,bg:"#F9FAFB"};
                const scfg=STATUS_COLORS[r.Status]||{dot:"#94A3B8",bg:"#F9FAFB"};
                return(
                  <tr key={r.ID||i} onClick={()=>setOpenIdx(i)} title="Click to view ticket details" style={{cursor:"pointer",background:i%2===0?T.card:T.bg,borderBottom:`1px solid ${T.border}`}}>
                    <td style={{padding:"9px 14px",fontWeight:700,color:T.blue,fontSize:12,whiteSpace:"nowrap"}}>{r.ID||"—"}</td>
                    <td style={{padding:"9px 14px",color:T.textSecondary,whiteSpace:"nowrap",fontSize:12}}>{r["Created On"]||"—"}</td>
                    <td style={{padding:"9px 14px",whiteSpace:"nowrap"}}>
                      <span style={{padding:"2px 8px",borderRadius:6,background:"#EFF6FF",color:"#2563EB",fontSize:11,fontWeight:600}}>{r.Company||"—"}</span>
                    </td>
                    <td style={{padding:"9px 14px",whiteSpace:"nowrap"}}>
                      <span style={{padding:"2px 8px",borderRadius:6,background:pcfg.bg,color:pcfg.color,fontSize:11,fontWeight:600}}>{r.Priority||"—"}</span>
                    </td>
                    <td style={{padding:"9px 14px",whiteSpace:"nowrap"}}>
                      <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"2px 9px",borderRadius:99,background:scfg.bg,color:scfg.dot,fontSize:11,fontWeight:600}}>
                        <span style={{width:6,height:6,borderRadius:"50%",background:scfg.dot,flexShrink:0}}/>
                        {STATUS_SHORT(r.Status||"")}
                      </span>
                    </td>
                    <td style={{padding:"9px 14px",color:T.textSecondary,fontSize:12,whiteSpace:"nowrap"}}>{r["Created By"]||"—"}</td>
                    <td style={{padding:"9px 14px",color:T.text,fontSize:12,maxWidth:300,wordBreak:"break-word"}}>{r.Description||"—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {tickets.length===0&&(
            <div style={{padding:"40px",textAlign:"center",color:T.textTertiary,fontSize:14}}>{t('solm.noTickets')}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{padding:"12px 24px",borderTop:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,background:T.bg}}>
          <span style={{fontSize:12,color:T.textSecondary}}>{tickets.length} {t('solm.totalTicket')}{tickets.length!==1?"s":""}</span>
          <button onClick={onClose} style={{fontSize:13,padding:"7px 18px",borderRadius:8,border:`1px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer"}}>{t('solm.close')}</button>
        </div>
      </div>
      {openIdx!=null && tickets[openIdx] && <TicketDetailModal row={tickets[openIdx]} onClose={()=>setOpenIdx(null)} T={T} z={1100} pos={`${openIdx+1} / ${tickets.length}`} onPrev={openIdx>0?()=>setOpenIdx(openIdx-1):null} onNext={openIdx<tickets.length-1?()=>setOpenIdx(openIdx+1):null}/>}
    </>
  );
}

/* ══════════════════════════════════════════════
   EXCEL-STYLE PIVOT: Impact (rows) × Status (sub-rows) × Month (cols)
══════════════════════════════════════════════ */
function ExcelPivot({filtered}){
  const { t: tr } = useLang();
  const[collapsed,setCollapsed]=useState({});
  const[sel,setSel]=useState(null);  // {team,status,monthLabel,tickets}

  const toggle=team=>setCollapsed(p=>({...p,[team]:!p[team]}));
  const expandAll =()=>setCollapsed({});
  const collapseAll=()=>{const all={};teams.forEach(t=>{all[t]=true;});setCollapsed(all);};

  /* open drawer: get tickets for team+status+optional monthKey */
  const openDrawer=(team,status,monthKey,monthLabel)=>{
    const tickets=filtered.filter(r=>{
      const matchTeam=(r.Impact||"Unknown")===team;
      const matchStatus=status==="__all__"?true:r.Status===status;
      const matchMonth=monthKey?r._monthKey===monthKey:true;
      return matchTeam&&matchStatus&&matchMonth;
    });
    setSel({team,status:status==="__all__"?"All statuses":status,monthLabel:monthLabel||null,tickets});
  };

  const months=useMemo(()=>{
    const map={};
    filtered.forEach(r=>{if(r._monthKey)map[r._monthKey]={key:r._monthKey,label:r._monthLabel,date:r._date};});
    return Object.values(map).sort((a,b)=>a.date-b.date);
  },[filtered]);

  const teams=useMemo(()=>{
    const cnt={};
    filtered.forEach(r=>{const t=r.Impact||"Unknown";cnt[t]=(cnt[t]||0)+1;});
    return Object.keys(cnt).sort((a,b)=>cnt[b]-cnt[a]);
  },[filtered]);

  const {matrix,teamTotals,statusTotals,colTotals,grandTotal}=useMemo(()=>{
    const matrix={},teamTotals={},statusTotals={},colTotals={};
    let grandTotal=0;
    teams.forEach(t=>{
      matrix[t]={};teamTotals[t]=0;
      STATUS_LIST.forEach(s=>{matrix[t][s]={};statusTotals[`${t}||${s}`]={total:0};months.forEach(m=>{matrix[t][s][m.key]=0;});});
    });
    months.forEach(m=>{colTotals[m.key]=0;});
    filtered.forEach(r=>{
      const t=r.Impact||"Unknown",s=r.Status||"Unknown",mk=r._monthKey;
      if(!mk||!matrix[t]||!matrix[t][s])return;
      matrix[t][s][mk]++;teamTotals[t]++;statusTotals[`${t}||${s}`].total++;colTotals[mk]=(colTotals[mk]||0)+1;grandTotal++;
    });
    return{matrix,teamTotals,statusTotals,colTotals,grandTotal};
  },[filtered,teams,months]);

  const maxTeamTotal=Math.max(...Object.values(teamTotals),1);
  const teamHeat=v=>{if(!v)return"transparent";const a=0.06+(v/maxTeamTotal)*0.55;return`rgba(37,99,235,${a.toFixed(2)})`;};
  const activeStatuses=team=>STATUS_LIST.filter(s=>statusTotals[`${team}||${s}`]?.total>0);

  const yearGroups=useMemo(()=>{
    const yg={};months.forEach(m=>{const y=String(m.date.getFullYear());yg[y]=(yg[y]||0)+1;});
    return Object.entries(yg);
  },[months]);

  if(!teams.length||!months.length)return null;

  /* Column widths — fixed so sticky math is exact */
  const COL_TEAM   = 180;
  const COL_STATUS = 130;
  const COL_MONTH  = 82;
  const COL_TOTAL  = 76;
  const FREEZE_W   = COL_TEAM + COL_STATUS; // 310px

  const thBase={fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:"0.05em",
    whiteSpace:"nowrap",textAlign:"center",color:T.textSecondary,
    borderBottom:`2px solid #334155`,padding:"10px 10px"};

  const clickCell=(v,bg,color)=>({
    padding:"8px 6px",textAlign:"center",
    background:bg||"transparent",color:color||T.text,
    fontWeight:v?700:400,borderBottom:`1px solid ${T.border}`,
    fontSize:13,whiteSpace:"nowrap",
    cursor:v?"pointer":"default",
    width:COL_MONTH,minWidth:COL_MONTH,maxWidth:COL_MONTH,
  });

  /* shadow on frozen cols to indicate scrollability */
  const freezeShadow="4px 0 8px rgba(0,0,0,0.08)";

  return(
    <>
      <TicketDrawer sel={sel} onClose={()=>setSel(null)}/>

      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden"}}>
        {/* Card header */}
        <div style={{padding:"14px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
          <div>
            <h3 style={{fontSize:14,fontWeight:700,color:T.text,margin:0}}>{tr('solm.pivotTitle')}</h3>
            <p style={{fontSize:12,color:T.textTertiary,margin:"3px 0 0"}}>
              {tr('solm.clickAny')} {tr('solm.toViewDetails')}
              <i className="ti ti-arrows-horizontal" style={{fontSize:12,marginLeft:6,marginRight:4,verticalAlign:-1}}/> {tr('solm.scrollHorizontal')}
            </p>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontSize:11,color:T.textTertiary,padding:"3px 10px",border:`1px solid ${T.border}`,borderRadius:6,background:T.bg}}>
              {months.length} {tr('solm.monthsWord')} · {teams.length} {tr('solm.teamsWord')}
            </span>
            <button onClick={expandAll} style={{fontSize:12,padding:"5px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:T.bg,color:T.textSecondary,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
              <i className="ti ti-chevrons-down" style={{fontSize:13}}/> Expand
            </button>
            <button onClick={collapseAll} style={{fontSize:12,padding:"5px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:T.bg,color:T.textSecondary,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
              <i className="ti ti-chevrons-right" style={{fontSize:13}}/> Collapse
            </button>
          </div>
        </div>

        {/* Scroll hint bar */}
        <div style={{padding:"6px 20px",background:"#F8FAFC",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:8}}>
          <div style={{display:"flex",gap:3}}>
            {[T.green,T.blue,T.amber,T.purple,T.teal,"#EA580C","#6B7280"].map((c,i)=>(
              <span key={i} style={{width:10,height:10,borderRadius:2,background:c,display:"inline-block"}}/>
            ))}
          </div>
          <span style={{fontSize:11,color:T.textTertiary}}>Colors = status type · Frozen: Team + Status columns · Scroll right for all {months.length} months</span>
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:4,fontSize:11,color:T.textTertiary}}>
            <i className="ti ti-lock" style={{fontSize:12,color:T.blue}}/> 2 columns frozen
          </div>
        </div>

        {/* Table wrapper — always show scrollbar */}
        <div style={{
          overflowX:"scroll",
          overflowY:"visible",
          /* always visible scrollbar */
          scrollbarWidth:"auto",
          scrollbarColor:`#94A3B8 #F1F5F9`,
          WebkitOverflowScrolling:"touch",
        }}>
          <table style={{borderCollapse:"collapse",fontSize:13,tableLayout:"fixed",width:`${FREEZE_W + months.length*COL_MONTH + COL_TOTAL}px`}}>
            <colgroup>
              <col style={{width:COL_TEAM}}/>
              <col style={{width:COL_STATUS}}/>
              {months.map(m=><col key={m.key} style={{width:COL_MONTH}}/>)}
              <col style={{width:COL_TOTAL}}/>
            </colgroup>
            <thead style={{position:"sticky",top:0,zIndex:4}}>
              {/* Year row */}
              <tr style={{background:"#1E3A5F"}}>
                <th style={{...thBase,textAlign:"left",position:"sticky",left:0,background:"#1E3A5F",zIndex:5,color:"#F1F5F9",padding:"8px 14px",boxShadow:freezeShadow}}>
                  Support Team (Impact)
                </th>
                <th style={{...thBase,position:"sticky",left:COL_TEAM,background:"#1E3A5F",zIndex:5,color:"#94A3B8",boxShadow:freezeShadow,borderRight:`1px solid #334155`}}>
                  Status
                </th>
                {yearGroups.map(([yr,cnt])=>(
                  <th key={yr} colSpan={cnt} style={{...thBase,background:"#1E3A5F",color:"#38BDF8",fontSize:12,letterSpacing:"0.08em"}}>{yr}</th>
                ))}
                <th style={{...thBase,background:"#1E293B",color:"#38BDF8",padding:"8px 10px",position:"sticky",right:0,zIndex:5}}>{tr('solm.totalCaps')}</th>
              </tr>
              {/* Month row */}
              <tr style={{background:"#F1F5F9"}}>
                <th style={{...thBase,textAlign:"left",position:"sticky",left:0,background:"#F1F5F9",zIndex:5,color:T.text,padding:"9px 14px",borderBottom:`1px solid ${T.border}`,boxShadow:freezeShadow}}></th>
                <th style={{...thBase,position:"sticky",left:COL_TEAM,background:"#F1F5F9",zIndex:5,color:T.textSecondary,borderBottom:`1px solid ${T.border}`,boxShadow:freezeShadow,borderRight:`1px solid ${T.border}`}}></th>
                {months.map(m=>(
                  <th key={m.key} style={{...thBase,background:"#F1F5F9",color:T.text,borderBottom:`1px solid ${T.border}`,fontSize:10,padding:"9px 4px"}}>{m.label}</th>
                ))}
                <th style={{...thBase,background:T.blueBg,color:T.blue,borderBottom:`1px solid ${T.border}`,position:"sticky",right:0,zIndex:5}}>{tr('solm.total')}</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team,ti)=>{
                const isCollapsed=!!collapsed[team];
                const statuses=activeStatuses(team);
                const rowBg=ti%2===0?T.card:"#FAFBFC";

                return(
                  <Fragment key={team}>
                    {/* Team row */}
                    <tr style={{background:rowBg,cursor:"pointer"}} onClick={()=>toggle(team)}>
                      <td style={{padding:"9px 14px",fontWeight:700,color:T.text,borderBottom:`1px solid ${T.border}`,position:"sticky",left:0,background:rowBg,zIndex:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",boxShadow:freezeShadow}}>
                        <div style={{display:"flex",alignItems:"center",gap:7}}>
                          <span style={{width:16,height:16,borderRadius:4,background:T.blueBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            <i className={`ti ${isCollapsed?"ti-chevron-right":"ti-chevron-down"}`} style={{fontSize:10,color:T.blue}}/>
                          </span>
                          <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{team}</span>
                        </div>
                      </td>
                      <td style={{padding:"9px 10px",borderBottom:`1px solid ${T.border}`,position:"sticky",left:COL_TEAM,background:rowBg,zIndex:2,boxShadow:freezeShadow,borderRight:`1px solid ${T.border}`}}>
                        <span style={{fontSize:10,color:T.textTertiary,fontStyle:"italic"}}>{tr('solm.allStatuses')}</span>
                      </td>
                      {months.map(m=>{
                        const tv=STATUS_LIST.reduce((s,st)=>s+(matrix[team]?.[st]?.[m.key]||0),0);
                        return(
                          <td key={m.key}
                            onClick={e=>{e.stopPropagation();if(tv)openDrawer(team,"__all__",m.key,m.label);}}
                            style={{...clickCell(tv,teamHeat(tv),tv?T.text:T.textTertiary)}}
                            title={tv?`${team} · All statuses · ${m.label} — ${tv} tickets`:""}>
                            {tv?<b>{tv}</b>:<span style={{color:"#D1D5DB",fontSize:11}}>—</span>}
                          </td>
                        );
                      })}
                      <td onClick={e=>{e.stopPropagation();if(teamTotals[team])openDrawer(team,"__all__",null,null);}}
                        style={{padding:"9px 10px",textAlign:"center",fontWeight:800,color:"#fff",background:T.blue,borderBottom:`1px solid ${T.border}`,fontSize:14,cursor:teamTotals[team]?"pointer":"default",position:"sticky",right:0,zIndex:2}}
                        title={`${team} — ${teamTotals[team]} total tickets`}>
                        {teamTotals[team]}
                      </td>
                    </tr>

                    {/* Status sub-rows */}
                    {!isCollapsed&&statuses.map(status=>{
                      const cfg=STATUS_COLORS[status]||{dot:"#94A3B8",bg:"#F9FAFB"};
                      const stTotal=statusTotals[`${team}||${status}`]?.total||0;
                      return(
                        <tr key={`${team}-${status}`} style={{background:rowBg}}>
                          <td style={{padding:"6px 14px 6px 36px",color:T.textSecondary,borderBottom:`1px solid ${T.border}`,position:"sticky",left:0,background:rowBg,zIndex:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",fontSize:12,boxShadow:freezeShadow}}>
                            <div style={{display:"flex",alignItems:"center",gap:5}}>
                              <span style={{width:6,height:6,borderRadius:"50%",background:cfg.dot,flexShrink:0}}/>
                              {STATUS_SHORT(status)}
                            </div>
                          </td>
                          <td style={{padding:"6px 10px",borderBottom:`1px solid ${T.border}`,position:"sticky",left:COL_TEAM,background:rowBg,zIndex:2,boxShadow:freezeShadow,borderRight:`1px solid ${T.border}`}}>
                            <span style={{display:"inline-block",padding:"1px 7px",borderRadius:99,background:cfg.bg,color:cfg.dot,fontSize:10,fontWeight:600,whiteSpace:"nowrap"}}>{STATUS_SHORT(status)}</span>
                          </td>
                          {months.map(m=>{
                            const v=matrix[team]?.[status]?.[m.key]||0;
                            return(
                              <td key={m.key}
                                onClick={()=>{if(v)openDrawer(team,status,m.key,m.label);}}
                                style={{padding:"6px 6px",textAlign:"center",borderBottom:`1px solid ${T.border}`,
                                  background:v?cfg.bg:"transparent",cursor:v?"pointer":"default",width:COL_MONTH}}
                                title={v?`${team} · ${STATUS_SHORT(status)} · ${m.label} — ${v} tickets`:""}
                                onMouseEnter={e=>{if(v)e.currentTarget.style.filter="brightness(0.93)";}}
                                onMouseLeave={e=>{e.currentTarget.style.filter="";}} >
                                {v
                                  ?<span style={{color:cfg.dot,fontWeight:700,fontSize:12,textDecoration:"underline dotted"}}>{v}</span>
                                  :<span style={{color:"#D1D5DB",fontSize:11}}>—</span>}
                              </td>
                            );
                          })}
                          <td onClick={()=>{if(stTotal)openDrawer(team,status,null,null);}}
                            style={{padding:"6px 10px",textAlign:"center",fontWeight:700,color:cfg.dot,background:cfg.bg,
                              borderBottom:`1px solid ${T.border}`,fontSize:12,
                              cursor:stTotal?"pointer":"default",
                              position:"sticky",right:0,zIndex:2,
                              textDecoration:stTotal?"underline dotted":"none"}}
                            title={stTotal?`${team} · ${STATUS_SHORT(status)} — ${stTotal} total tickets`:""}
                            onMouseEnter={e=>{if(stTotal)e.currentTarget.style.filter="brightness(0.93)";}}
                            onMouseLeave={e=>{e.currentTarget.style.filter="";}}>
                            {stTotal||"—"}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}

              {/* Grand total row */}
              <tr style={{background:"#1E3A5F",borderTop:`2px solid #334155`}}>
                <td colSpan={2} style={{padding:"10px 14px",fontWeight:800,color:"#F1F5F9",position:"sticky",left:0,background:"#1E3A5F",zIndex:2,fontSize:13,boxShadow:freezeShadow}}>
                  Total
                </td>
                {months.map(m=>(
                  <td key={m.key} style={{padding:"10px 6px",textAlign:"center",fontWeight:700,color:"#F1F5F9",fontSize:13}}>
                    {colTotals[m.key]||<span style={{color:"#475569",fontSize:11}}>—</span>}
                  </td>
                ))}
                <td style={{padding:"10px 10px",textAlign:"center",fontWeight:900,color:"#38BDF8",fontSize:15,position:"sticky",right:0,background:"#1E3A5F",zIndex:2}}>
                  {grandTotal}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Bottom scroll shadow indicator */}
        <div style={{height:3,background:`linear-gradient(to right, ${T.blue}22, ${T.blue}44, ${T.blue}22)`,borderTop:`1px solid ${T.border}`}}/>
      </div>
    </>
  );
}

/* ── Pivot Table ───────────────────────────────── */
function PivotTable({filtered}){
  const { t: tr } = useLang();
  const[rowDim,setRowDim]=useState("Impact");
  const[colDim,setColDim]=useState("month");
  const DIMS=[
    {value:"Impact",  label:"Support team (Impact)"},
    {value:"Company", label:"Company"},
    {value:"Status",  label:"Status"},
    {value:"Priority",label:"Priority"},
  ];
  const COL_DIMS=[
    {value:"month",   label:"Month (Created On)"},
    {value:"year",    label:"Year"},
    {value:"Status",  label:"Status"},
    {value:"Company", label:"Company"},
  ];
  const pivot=useMemo(()=>{
    const getCol=r=>{
      if(colDim==="month")return r._monthLabel||"Unknown";
      if(colDim==="year") return String(r._year)||"Unknown";
      return r[colDim]||"Unknown";
    };
    const rows=[...new Set(filtered.map(r=>r[rowDim]||"Unknown"))].sort();
    const colsRaw=[...new Set(filtered.map(getCol))].filter(Boolean);
    const cols=colDim==="month"?colsRaw.sort((a,b)=>{
      const da=filtered.find(r=>r._monthLabel===a)?._date;
      const db=filtered.find(r=>r._monthLabel===b)?._date;
      return da-db;
    }):colsRaw.sort();
    const matrix={},rowTotals={},colTotals={};
    rows.forEach(r=>{matrix[r]={};rowTotals[r]=0;});
    cols.forEach(c=>{colTotals[c]=0;});
    filtered.forEach(t=>{
      const rv=t[rowDim]||"Unknown",cv=getCol(t);
      if(!matrix[rv])return;
      matrix[rv][cv]=(matrix[rv][cv]||0)+1;
      rowTotals[rv]++;
      colTotals[cv]=(colTotals[cv]||0)+1;
    });
    const maxVal=Math.max(...Object.values(matrix).flatMap(r=>Object.values(r)),1);
    return{rows,cols,matrix,rowTotals,colTotals,maxVal,grand:filtered.length};
  },[filtered,rowDim,colDim]);

  const heatBg=(v,max)=>{if(!v)return"transparent";const a=0.08+(v/max)*0.78;return`rgba(37,99,235,${a.toFixed(2)})`;};
  const heatTx=(v,max)=>v/max>0.48?"#fff":T.text;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:16,alignItems:"center",background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"14px 20px",flexWrap:"wrap"}}>
        <i className="ti ti-settings-2" style={{fontSize:16,color:T.textTertiary}}/>
        {[{label:"Rows",val:rowDim,set:setRowDim,opts:DIMS},{label:"Columns",val:colDim,set:setColDim,opts:COL_DIMS}].map(ctrl=>(
          <div key={ctrl.label} style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:13,fontWeight:600,color:T.textSecondary}}>{ctrl.label}</span>
            <select value={ctrl.val} onChange={e=>ctrl.set(e.target.value)} style={{fontSize:13,padding:"6px 10px",borderRadius:8,border:`1px solid ${T.border}`,background:T.bg,color:T.text,cursor:"pointer"}}>
              {ctrl.opts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        ))}
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8,fontSize:12,color:T.textTertiary}}>
          <div style={{width:80,height:8,borderRadius:4,background:"linear-gradient(to right,rgba(37,99,235,0.1),rgba(37,99,235,0.85))"}}/>
          Low → High density
        </div>
      </div>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"auto"}}>
        <table style={{borderCollapse:"collapse",fontSize:12,width:"100%"}}>
          <thead>
            <tr style={{background:T.bg}}>
              <th style={{padding:"12px 18px",textAlign:"left",fontWeight:700,borderBottom:`1px solid ${T.border}`,position:"sticky",left:0,background:T.bg,zIndex:2,color:T.textSecondary,fontSize:11,textTransform:"uppercase",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>
                {DIMS.find(d=>d.value===rowDim)?.label}
              </th>
              {pivot.cols.map(col=>(
                <th key={col} style={{padding:"12px 8px",textAlign:"center",fontWeight:600,borderBottom:`1px solid ${T.border}`,whiteSpace:"nowrap",minWidth:66,color:T.textSecondary,fontSize:11}}>{col}</th>
              ))}
              <th style={{padding:"12px 16px",textAlign:"center",fontWeight:800,borderBottom:`1px solid ${T.border}`,background:T.blueBg,color:T.blue,fontSize:12,textTransform:"uppercase"}}>{tr('solm.total')}</th>
            </tr>
          </thead>
          <tbody>
            {pivot.rows.map((row,i)=>(
              <tr key={row} style={{borderBottom:`1px solid ${T.border}`}}>
                <td style={{padding:"9px 18px",fontWeight:600,position:"sticky",left:0,background:i%2===0?T.card:T.bg,zIndex:1,whiteSpace:"nowrap",fontSize:13,color:T.text}}>{row}</td>
                {pivot.cols.map(col=>{
                  const val=pivot.matrix[row][col]||0;
                  return(
                    <td key={col} style={{padding:"9px 8px",textAlign:"center",background:heatBg(val,pivot.maxVal)}}>
                      <span style={{color:val?heatTx(val,pivot.maxVal):T.border,fontWeight:val?700:400}}>{val||"—"}</span>
                    </td>
                  );
                })}
                <td style={{padding:"9px 16px",textAlign:"center",fontWeight:800,color:T.blue,background:T.blueBg,fontSize:14}}>{pivot.rowTotals[row]}</td>
              </tr>
            ))}
            <tr style={{background:T.bg,borderTop:`2px solid ${T.border}`}}>
              <td style={{padding:"11px 18px",fontWeight:800,position:"sticky",left:0,background:T.bg,fontSize:13,color:T.text}}>{tr('solm.total')}</td>
              {pivot.cols.map(col=>(
                <td key={col} style={{padding:"11px 8px",textAlign:"center",fontWeight:700,color:T.text}}>{pivot.colTotals[col]||"—"}</td>
              ))}
              <td style={{padding:"11px 16px",textAlign:"center",fontWeight:900,color:T.blue,background:T.blueBg,fontSize:15}}>{pivot.grand}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   CUSTOM HOOK — useSolmanData
   Encapsulates all state & computed data.
   Can be used standalone by a parent component.

   Usage in parent:
     const ref = useRef();
     <SolmanDashboard ref={ref} onDataChange={({filtered,stats,data,filters})=>{...}}/>
     // later: ref.current.getFiltered(), ref.current.getStats(), ref.current.getData()
     // or: ref.current.setFilter({company:"OSI", year:"2026"})
══════════════════════════════════════════════ */
/* ── Tab Detail Tiket ───────────────────────────────────────────────────────
   Tabel 23 kolom EXPORT_HEADERS di atas `filtered` (hasil filter bar atas),
   ditambah: pencarian bebas, filter per kolom, sort per kolom, paginasi,
   pemilih kolom, dan export Excel yang mengikuti tampilan saat itu.

   Semua di klien — `filtered` sudah ada di memori dan dipakai bersama tab lain,
   jadi tidak ada request tambahan. Untuk ~4.500 baris, biaya filter+sort per
   ketikan masih di bawah beberapa milidetik; yang mahal adalah RENDER (23 kolom
   × ribuan baris = ratusan ribu sel), karena itu barisnya DIPAGINASI. Sengaja
   bukan virtual-scroll: itu menuntut dependency baru, sedangkan paginasi cukup
   dan tetap satu file. */

// Default 10 dari 23 kolom supaya tabel tidak langsung sesak.
const DETAIL_DEFAULT_COLS = ["ID","Created On","Company","Priority","Status","Impact","Service Team","Processor","Created By","Description"];
// Kolom dengan nilai unik <= ambang ini difilter lewat dropdown; selebihnya
// pakai kotak teks (Description/Reporter hampir selalu unik → dropdown tak ada
// gunanya di sana). Ditentukan dari data, bukan daftar hardcode, supaya ikut
// menyesuaikan kalau isi tabel berubah.
const DETAIL_SELECT_MAX = 25;
const DETAIL_PAGE_SIZES = [50,100,200,500];

// Tipe kolom → menentukan cara sort dan cara sel ditulis ke Excel.
function detailColType(h){
  if(EXPORT_NUMERIC_COLS.has(h)) return "num";
  if(h==="Created On"||h==="Created On2") return "date";
  return "text";
}
// Buang karakter yang tidak sah untuk nama file di Windows/Linux.
function safeFileName(s){ return String(s).replace(/[\\/:*?"<>|]+/g,"-").replace(/\s+/g," ").trim(); }

function TicketDetail({filtered, filters}){
  const { t } = useLang();

  const [q,setQ]                 = useState("");
  const [colFilters,setColF]     = useState({});          // {header: nilai}
  const [sortCol,setSortCol]     = useState("ID");
  const [sortDir,setSortDir]     = useState(-1);           // 1 naik · -1 turun
  const [page,setPage]           = useState(1);
  const [pageSize,setPageSize]   = useState(100);
  const [visible,setVisible]     = useState(DETAIL_DEFAULT_COLS);
  const [showCols,setShowCols]   = useState(false);
  const [openRow,setOpenRow] = useState(null);

  // Nilai unik per kolom → menentukan dropdown vs kotak teks, sekaligus isi
  // dropdown-nya. Dihitung dari `filtered` supaya pilihannya selalu relevan
  // dengan filter atas yang sedang aktif.
  const distinct = useMemo(()=>{
    const out = {};
    EXPORT_HEADERS.forEach(h=>{
      const set = new Set();
      for(const r of filtered){
        set.add(String(r[h] ?? "").trim());
        if(set.size > DETAIL_SELECT_MAX + 1) break;   // cukup tahu "terlalu banyak"
      }
      out[h] = set.size <= DETAIL_SELECT_MAX
        ? [...set].filter(v=>v!=="").sort((a,b)=>a.localeCompare(b,undefined,{numeric:true}))
        : null;                                        // null = pakai kotak teks
    });
    return out;
  },[filtered]);

  // Pencarian bebas menyapu SELURUH 23 kolom, bukan hanya kolom yang tampil —
  // supaya mengetik nomor tiket atau nama Processor tetap menemukan barisnya
  // walau kolom itu sedang disembunyikan.
  const searched = useMemo(()=>{
    const needle = q.trim().toLowerCase();
    const active = Object.entries(colFilters).filter(([,v])=>String(v).trim()!=="");
    if(!needle && active.length===0) return filtered;
    return filtered.filter(r=>{
      if(needle){
        let hit = false;
        for(const h of EXPORT_HEADERS){
          if(String(r[h] ?? "").toLowerCase().includes(needle)){ hit = true; break; }
        }
        if(!hit) return false;
      }
      for(const [h,v] of active){
        const cell = String(r[h] ?? "").toLowerCase();
        const want = String(v).toLowerCase();
        // Dropdown = cocok persis; kotak teks = mengandung.
        if(distinct[h] ? cell !== want : !cell.includes(want)) return false;
      }
      return true;
    });
  },[filtered,q,colFilters,distinct]);

  const sorted = useMemo(()=>{
    if(!sortDir || !sortCol) return searched;
    const type = detailColType(sortCol);
    const arr = [...searched];
    arr.sort((a,b)=>{
      const va = a[sortCol], vb = b[sortCol];
      let c;
      if(type==="num"){
        // Sel kosong TIDAK dianggap 0 — kalau dianggap 0 ia melompat ke atas saat
        // sort naik. Diperlakukan sebagai "tak bernomor" supaya turun ke bawah,
        // sama seperti perilaku sort Excel terhadap sel kosong.
        const na = (va===""||va==null) ? NaN : Number(va);
        const nb = (vb===""||vb==null) ? NaN : Number(vb);
        const oka = Number.isFinite(na), okb = Number.isFinite(nb);
        // Nilai tak bernomor selalu di bawah, apa pun arah sortnya.
        if(!oka && !okb) c = 0; else if(!oka) return 1; else if(!okb) return -1; else c = na - nb;
      }else if(type==="date"){
        const da = parseAnyDate(va), db = parseAnyDate(vb);
        if(!da && !db) c = 0; else if(!da) return 1; else if(!db) return -1; else c = da - db;
      }else{
        c = String(va ?? "").localeCompare(String(vb ?? ""),undefined,{numeric:true,sensitivity:"base"});
      }
      return sortDir * c;
    });
    return arr;
  },[searched,sortCol,sortDir]);

  // Halaman dijaga tetap valid saat jumlah baris menyusut karena filter.
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const curPage    = Math.min(page, totalPages);
  const from       = sorted.length === 0 ? 0 : (curPage-1)*pageSize + 1;
  const to         = Math.min(curPage*pageSize, sorted.length);
  const paged      = useMemo(()=>sorted.slice((curPage-1)*pageSize, curPage*pageSize),[sorted,curPage,pageSize]);

  const clickHeader = (h)=>{
    // Kolom baru → naik; kolom yang sama → balik arah. Sengaja TANPA keadaan
    // "mati": keadaan awal sudah turun, jadi siklus tiga-keadaan membuat klik
    // pertama justru mematikan sort — membingungkan, dan "urutan asli" praktis
    // tak pernah dibutuhkan di tabel ini.
    if(sortCol!==h){ setSortCol(h); setSortDir(1); return; }
    setSortDir(d=> d===1 ? -1 : 1);
  };
  const setColFilter = (h,v)=>{ setColF(p=>({...p,[h]:v})); setPage(1); };
  const clearAll = ()=>{ setQ(""); setColF({}); setPage(1); };
  const toggleCol = (h)=>setVisible(p=> p.includes(h) ? p.filter(x=>x!==h) : EXPORT_HEADERS.filter(x=>p.includes(x)||x===h));
  const activeFilterCount = Object.values(colFilters).filter(v=>String(v).trim()!=="").length + (q.trim()?1:0);

  /* ── Export: mengikuti kolom terpilih + hasil ayakan + urutan sort ──
     Gaya header hijau disamakan dengan export laporan bulanan agar konsisten
     dibaca, TAPI nama filenya dibedakan supaya file ad-hoc ini tidak pernah
     tertukar dengan file laporan resmi (`MM. Solman tickets Bulan yyyy - CO.xlsx`). */
  const doExport = useCallback(()=>{
    const cols = EXPORT_HEADERS.filter(h=>visible.includes(h));
    if(cols.length===0 || sorted.length===0) return;
    const rows = sorted.map(r=>{
      const out = {};
      cols.forEach(h=>{
        const v = r[h];
        if(detailColType(h)==="date"){ out[h] = parseAnyDate(v) || v || ""; }
        else if(detailColType(h)==="num"){
          const n = Number(v);
          out[h] = (v!==""&&v!=null&&Number.isFinite(n)) ? n : (v||"");
        }else out[h] = v ?? "";
      });
      return out;
    });
    // cellDates WAJIB: tanpa ini json_to_sheet mengonversi Date → angka serial
    // lebih dulu, sehingga post-pass `v instanceof Date` di bawah tidak pernah
    // kena dan kolom tanggal keluar dengan format m/d/yy, bukan d-mmm-yyyy.
    const ws = XLSX.utils.json_to_sheet(rows,{header:cols,cellDates:true});
    ws['!cols'] = cols.map(h=>({wch:EXPORT_COL_WIDTHS[h]||14}));
    ws['!autofilter'] = {ref:XLSX.utils.encode_range({s:{r:0,c:0},e:{r:rows.length,c:cols.length-1}})};
    ws['!freeze'] = {xSplit:0,ySplit:1};
    const range = XLSX.utils.decode_range(ws['!ref']);
    const HEADER_STYLE = {
      font: { name:"Aptos Narrow", sz:11, bold:true, color:{rgb:"FFFFFFFF"} },
      fill: { patternType:"solid", fgColor:{rgb:"FF4EA72E"} },
    };
    for(let C=range.s.c;C<=range.e.c;C++){
      const ref = XLSX.utils.encode_cell({r:range.s.r,c:C});
      if(ws[ref]) ws[ref].s = HEADER_STYLE;
    }
    cols.forEach((h,ci)=>{
      if(detailColType(h)!=="date") return;
      for(let R=range.s.r+1;R<=range.e.r;R++){
        const ref = XLSX.utils.encode_cell({r:R,c:ci});
        if(ws[ref] && ws[ref].v instanceof Date){ ws[ref].t='d'; ws[ref].z='[$-409]d\\-mmm\\-yyyy;@'; delete ws[ref].w; }
      }
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Detail");

    // Penanda filter aktif ikut masuk nama file — supaya file yang sudah
    // diunduh masih bisa dipertanggungjawabkan tanpa membuka isinya.
    const d = new Date();
    const p2 = n=>String(n).padStart(2,"0");
    const stamp = `${d.getFullYear()}-${p2(d.getMonth()+1)}-${p2(d.getDate())} ${p2(d.getHours())}${p2(d.getMinutes())}`;
    const bits = [];
    if(filters){
      if(filters.company && filters.company!=="All") bits.push(filters.company);
      if(filters.team    && filters.team!=="All")    bits.push(filters.team);
      if(Array.isArray(filters.year) && filters.year.length) bits.push(filters.year.join("+"));
      if(filters.status  && filters.status!=="All")  bits.push(filters.status==="__OPEN__"?"Open":STATUS_SHORT(filters.status));
    }
    if(q.trim()) bits.push(`"${q.trim()}"`);
    bits.push(`${sorted.length} rows`);
    XLSX.writeFile(wb, safeFileName(`Solman detail ${stamp} (${bits.join(", ")}).xlsx`));
  },[sorted,visible,filters,q]);

  const thBase = {padding:"9px 12px",textAlign:"left",fontWeight:700,color:T.textSecondary,borderBottom:`1px solid ${T.border}`,fontSize:11,textTransform:"uppercase",letterSpacing:"0.04em",whiteSpace:"nowrap",cursor:"pointer",userSelect:"none",background:"#F1F5F9",position:"sticky",top:0,zIndex:2};
  const tdBase = {padding:"7px 12px",borderBottom:`1px solid ${T.border}`,fontSize:12.5,color:T.text,whiteSpace:"nowrap",maxWidth:340,overflow:"hidden",textOverflow:"ellipsis"};
  const ctrl   = {padding:"6px 9px",fontSize:12,border:`1px solid ${T.border}`,borderRadius:7,fontFamily:"inherit",background:T.card,color:T.text};

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* ── Baris kontrol ── */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        <input value={q} onChange={e=>{setQ(e.target.value);setPage(1);}} placeholder={t('solm.det.searchPh')}
          style={{...ctrl,width:280}}/>
        <button onClick={()=>setShowCols(s=>!s)} style={{...ctrl,cursor:"pointer",fontWeight:600}}>
          {t('solm.det.columns',{n:visible.length,total:EXPORT_HEADERS.length})}
        </button>
        {activeFilterCount>0 && (
          <button onClick={clearAll} style={{...ctrl,cursor:"pointer",color:T.red,borderColor:"#FECACA",background:T.redBg,fontWeight:600}}>
            {t('solm.det.clearFilters',{n:activeFilterCount})}
          </button>
        )}
        <div style={{marginLeft:"auto",display:"flex",gap:8,alignItems:"center"}}>
          <span style={{fontSize:12,color:T.textSecondary}}>{t('solm.det.perPage')}</span>
          <select value={pageSize} onChange={e=>{setPageSize(Number(e.target.value));setPage(1);}} style={{...ctrl,cursor:"pointer"}}>
            {DETAIL_PAGE_SIZES.map(n=><option key={n} value={n}>{n}</option>)}
          </select>
          <button onClick={doExport} disabled={sorted.length===0}
            style={{...ctrl,cursor:sorted.length?"pointer":"not-allowed",background:sorted.length?T.green:"#E2E8F0",color:sorted.length?"#fff":T.textTertiary,border:"none",fontWeight:700,padding:"7px 14px"}}>
            <i className="ti ti-file-export" style={{marginRight:6}}/>{t('solm.det.export')}
          </button>
        </div>
      </div>

      {/* ── Pemilih kolom ── */}
      {showCols && (
        <Card>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
            {EXPORT_HEADERS.map(h=>{
              const on = visible.includes(h);
              return(
                <button key={h} onClick={()=>toggleCol(h)}
                  style={{padding:"4px 10px",fontSize:11.5,borderRadius:99,cursor:"pointer",fontFamily:"inherit",fontWeight:on?700:500,
                    border:`1px solid ${on?"#A7F3D0":T.border}`,background:on?T.greenBg:T.card,color:on?T.green:T.textSecondary}}>
                  {on?"✓ ":""}{h}
                </button>
              );
            })}
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setVisible(EXPORT_HEADERS)} style={{...ctrl,cursor:"pointer"}}>{t('solm.det.colsAll')}</button>
            <button onClick={()=>setVisible(DETAIL_DEFAULT_COLS)} style={{...ctrl,cursor:"pointer"}}>{t('solm.det.colsDefault')}</button>
          </div>
        </Card>
      )}

      {/* ── Tabel ── */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
        <div style={{overflow:"auto",maxHeight:"64vh"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr>
                {EXPORT_HEADERS.filter(h=>visible.includes(h)).map(h=>(
                  <th key={h} onClick={()=>clickHeader(h)} title={t('solm.det.sortHint')} style={thBase}>
                    {h}
                    <span style={{marginLeft:5,color:sortCol===h?T.blue:T.textTertiary}}>
                      {sortCol===h ? (sortDir===1?"▲":"▼") : "↕"}
                    </span>
                  </th>
                ))}
              </tr>
              {/* Baris filter per kolom */}
              <tr>
                {EXPORT_HEADERS.filter(h=>visible.includes(h)).map(h=>(
                  <th key={h} style={{padding:"6px 8px",background:"#F8FAFC",borderBottom:`1px solid ${T.border}`,position:"sticky",top:34,zIndex:1}}>
                    {distinct[h] ? (
                      <select value={colFilters[h]||""} onChange={e=>setColFilter(h,e.target.value)} style={{...ctrl,width:"100%",fontSize:11,padding:"4px 6px",cursor:"pointer"}}>
                        <option value="">{t('solm.det.allValues')}</option>
                        {distinct[h].map(v=><option key={v} value={v}>{v}</option>)}
                      </select>
                    ) : (
                      <input value={colFilters[h]||""} onChange={e=>setColFilter(h,e.target.value)} placeholder={t('solm.det.filterPh')}
                        style={{...ctrl,width:"100%",fontSize:11,padding:"4px 6px"}}/>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length===0 ? (
                <tr><td colSpan={visible.length||1} style={{padding:"34px 12px",textAlign:"center",color:T.textTertiary,fontSize:13}}>{t('solm.det.noRows')}</td></tr>
              ) : paged.map((r,i)=>(
                <tr key={(r.ID??"")+"-"+i} onClick={()=>setOpenRow(r)} title="Click to view ticket details" style={{cursor:"pointer",background:i%2?"#FBFDFF":T.card}}>
                  {EXPORT_HEADERS.filter(h=>visible.includes(h)).map(h=>{
                    if(h==="Status"){
                      const cfg = STATUS_COLORS[r.Status]||{dot:"#94A3B8",bg:"#F9FAFB"};
                      return(
                        <td key={h} style={tdBase}>
                          <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"2px 8px",borderRadius:99,background:cfg.bg,color:cfg.dot,fontSize:11.5,fontWeight:700}}>
                            <span style={{width:6,height:6,borderRadius:"50%",background:cfg.dot}}/>{STATUS_SHORT(r.Status||"")}
                          </span>
                        </td>
                      );
                    }
                    if(h==="Priority"){
                      const pc = PRIORITY_CFG[r.Priority]||{color:T.textSecondary,bg:"#F9FAFB",label:r.Priority||""};
                      return(
                        <td key={h} style={tdBase}>
                          <span style={{padding:"2px 8px",borderRadius:6,background:pc.bg,color:pc.color,fontSize:11.5,fontWeight:700}}>{pc.label}</span>
                        </td>
                      );
                    }
                    const val = r[h];
                    return <td key={h} style={tdBase} title={String(val??"")}>{val==null||val===""?"—":String(val)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Paginasi ── */}
        <div style={{display:"flex",gap:8,alignItems:"center",padding:"10px 14px",borderTop:`1px solid ${T.border}`,background:"#F8FAFC",flexWrap:"wrap"}}>
          <span style={{fontSize:12,color:T.textSecondary}}>
            {t('solm.det.showing',{from,to,total:sorted.length})}
            {sorted.length!==filtered.length && <span style={{color:T.textTertiary}}> {t('solm.det.ofFiltered',{n:filtered.length})}</span>}
          </span>
          <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
            <button onClick={()=>setPage(1)}            disabled={curPage<=1} style={{...ctrl,cursor:curPage<=1?"default":"pointer",opacity:curPage<=1?0.45:1}}>«</button>
            <button onClick={()=>setPage(curPage-1)}     disabled={curPage<=1} style={{...ctrl,cursor:curPage<=1?"default":"pointer",opacity:curPage<=1?0.45:1}}>‹</button>
            <span style={{fontSize:12,color:T.text,fontWeight:600,minWidth:96,textAlign:"center"}}>{t('solm.det.pageOf',{page:curPage,total:totalPages})}</span>
            <button onClick={()=>setPage(curPage+1)}     disabled={curPage>=totalPages} style={{...ctrl,cursor:curPage>=totalPages?"default":"pointer",opacity:curPage>=totalPages?0.45:1}}>›</button>
            <button onClick={()=>setPage(totalPages)}    disabled={curPage>=totalPages} style={{...ctrl,cursor:curPage>=totalPages?"default":"pointer",opacity:curPage>=totalPages?0.45:1}}>»</button>
          </div>
        </div>
      </div>
      {openRow && (()=>{ const oi=paged.indexOf(openRow); return <TicketDetailModal row={openRow} onClose={()=>setOpenRow(null)} T={T} pos={oi>=0?`${oi+1} / ${paged.length}`:null} onPrev={oi>0?()=>setOpenRow(paged[oi-1]):null} onNext={oi>=0&&oi<paged.length-1?()=>setOpenRow(paged[oi+1]):null}/>; })()}
    </div>
  );
}


/* Ticket texts (Description / Reply / ...) — read LIVE from SolMan SM1 via
   GET /api/solman/tickets/:id/texts (FM Z_SOLMAN_TICKET_TEXT). Not stored in solman_tickets. */
function TicketTexts({id,T}){
  const auth=useAuth();
  const token=(auth&&auth.token)||localStorage.getItem("auth_token");
  const [state,setState]=useState({loading:true,error:null,entries:[]});
  const [reload,setReload]=useState(0);
  useEffect(()=>{
    if(!id){ setState({loading:false,error:null,entries:[]}); return; }
    let alive=true;
    setState(s=>({...s,loading:true,error:null}));
    const API_BASE=window.__API_BASE__||"";
    fetch(`${API_BASE}/api/solman/tickets/${encodeURIComponent(String(id).trim())}/texts`,{headers:{"Authorization":`Bearer ${token}`}})
      .then(async r=>{ const j=await r.json().catch(()=>({})); if(!r.ok) throw new Error(j.error||("HTTP "+r.status)); return j; })
      .then(j=>{ if(alive) setState({loading:false,error:null,entries:j.entries||[]}); })
      .catch(e=>{ if(alive) setState({loading:false,error:e.message,entries:[]}); });
    return ()=>{ alive=false; };
  },[id,token,reload]);
  const color=t=>t==="SU99"?T.blue:(t==="SU01"?"#059669":T.textSecondary);
  const when=e=>{
    if(e.created_on) return e.created_on.split("-").reverse().join(".")+" "+String(e.created_at||"").slice(0,5);
    const st=String(e.stamp||"");
    if(/^\d{14}/.test(st)) return `${st.slice(6,8)}.${st.slice(4,6)}.${st.slice(0,4)} ${st.slice(8,10)}:${st.slice(10,12)}`;
    return [e.created_on,e.created_at].filter(Boolean).join(" ");
  };
  return (
    <div style={{marginTop:6,borderTop:`1px solid ${T.border}`,paddingTop:12}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <div style={{fontSize:11,color:T.textTertiary,fontWeight:600,textTransform:"uppercase",letterSpacing:0.3,flex:1}}>
          Text Log {!state.loading&&!state.error&&`(${state.entries.length})`} <span style={{fontWeight:400,textTransform:"none"}}>· live from SM1</span>
        </div>
        <button onClick={()=>setReload(x=>x+1)} disabled={state.loading} title="Reload from SolMan"
          style={{padding:"3px 8px",borderRadius:6,border:`1px solid ${T.border}`,background:"transparent",color:T.textSecondary,cursor:"pointer",fontSize:11}}>
          {state.loading?"Loading…":"Reload"}
        </button>
      </div>
      {state.error&&<div style={{fontSize:12,color:"#dc2626",whiteSpace:"pre-wrap"}}>{state.error}</div>}
      {!state.loading&&!state.error&&state.entries.length===0&&<div style={{fontSize:12,color:T.textTertiary}}>No texts on this ticket.</div>}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {state.entries.map((e,i)=>(
          <div key={(e.stamp||"")+e.tdid+i} style={{borderLeft:`3px solid ${color(e.tdid)}`,padding:"6px 10px",background:"rgba(148,163,184,0.08)",borderRadius:4}}>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>
              <b style={{color:color(e.tdid)}}>{e.type}</b> · {when(e)} · {e.created_by}
            </div>
            <div style={{fontSize:13,color:T.text,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{e.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Close / Withdraw tiket Solman (POST /api/solman/tickets/:id/close|withdraw) ── */
function TicketStatusAction({row,T}){
  const auth=useAuth();
  const token=(auth&&auth.token)||localStorage.getItem("auth_token");
  const [mode,setMode]=useState(null);
  const [note,setNote]=useState("");
  const [busy,setBusy]=useState(false);
  const [result,setResult]=useState(null);
  const id=String(row.ID??row.ticket_id??"").trim();
  const status=String(row.Status??row.status??"");
  if(!id) return null;
  if(!(auth&&auth.hasAccess&&auth.hasAccess("solman_ticket_close"))) return null;  // admin / role khusus saja
  if(/^(closed|withdrawn)$/i.test(status.trim())&&!result) return null;
  const submit=()=>{
    setBusy(true); setResult(null);
    const API_BASE=window.__API_BASE__||"";
    fetch(`${API_BASE}/api/solman/tickets/${encodeURIComponent(id)}/${mode}`,{
      method:"POST",
      headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
      body:JSON.stringify({note})})
      .then(async r=>{ const j=await r.json().catch(()=>({})); if(!r.ok||!j.ok) throw new Error(j.error||j.message||("HTTP "+r.status)); return j; })
      .then(j=>{ if("Status" in row) row.Status=j.status; setResult({ok:true,msg:`Ticket ${id} is now ${j.status} in Solution Manager.`}); setMode(null); setNote(""); })
      .catch(e=>setResult({ok:false,msg:e.message}))
      .finally(()=>setBusy(false));
  };
  const btn=(bg,fg,bd)=>({padding:"6px 12px",borderRadius:6,border:`1px solid ${bd}`,background:bg,color:fg,fontSize:12,fontWeight:600,cursor:busy?"wait":"pointer",opacity:busy?0.6:1});
  const label=mode==="withdraw"?"withdraw":"close";
  return (
    <div style={{border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",display:"flex",flexDirection:"column",gap:8}}>
      {result&&(
        <div style={{fontSize:12,padding:"6px 10px",borderRadius:6,background:result.ok?"rgba(16,185,129,0.12)":"rgba(239,68,68,0.12)",color:result.ok?"#047857":"#b91c1c"}}>
          {result.ok?"✓ ":"✕ "}{result.msg}
        </div>
      )}
      {!mode&&!(result&&result.ok)&&(
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <button onClick={()=>{setMode("close");setResult(null);}} disabled={busy} style={btn("#059669","#fff","#059669")}>Close Ticket</button>
          <button onClick={()=>{setMode("withdraw");setResult(null);}} disabled={busy} style={btn("transparent",T.textSecondary,T.border)}>Withdraw</button>
          <span style={{fontSize:11,color:T.textTertiary}}>Updates the ticket status directly in SAP Solution Manager.</span>
        </div>
      )}
      {mode&&(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:12,fontWeight:600,color:T.text}}>{mode==="close"?"Close":"Withdraw"} ticket {id}?</div>
          <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3} maxLength={2000}
            placeholder={mode==="close"?"Closing note (optional) — added as a Reply on the ticket":"Reason for withdrawal (optional) — added as a Reply on the ticket"}
            style={{width:"100%",boxSizing:"border-box",fontSize:12,padding:8,borderRadius:6,border:`1px solid ${T.border}`,background:"transparent",color:T.text,resize:"vertical",fontFamily:"inherit"}}/>
          <div style={{fontSize:11,color:T.textTertiary}}>The reporter will see this reply in Solution Manager. This cannot be undone from the dashboard.</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={submit} disabled={busy} style={btn(mode==="close"?"#059669":"#dc2626","#fff",mode==="close"?"#059669":"#dc2626")}>{busy?"Processing…":`Confirm ${label}`}</button>
            <button onClick={()=>{setMode(null);setNote("");}} disabled={busy} style={btn("transparent",T.textSecondary,T.border)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   AI · PROPOSE SOLUTION — panel di modal tiket (izin solman_ai)
   Backend: backend/solmanAiPropose.js (read-only, hasil di-cache per tiket)
═══════════════════════════════════════════════════════════════ */
const AI_CAT_LABEL={data_correction:"Data correction",master_data:"Master data",configuration:"Configuration",authorization:"Authorization",program_bug:"Program bug",user_error:"User error / how-to",interface:"Interface",performance:"Performance",other:"Other"};
const AI_CHECK_LINK={tcode:"/dashboard/sap-roles?tab=tcodeuser",role:"/dashboard/sap-roles?tab=roleuser",object:"/dashboard/sap-source?tab=wb",config:"/dashboard/sap-customizing",doc:"/dashboard/solman-docs"};
const AI_DOC_LABEL={matdoc:"Material doc",lot:"Inspection lot",po:"Purchase order",so:"Sales order",order:"Order",mat:"Material",mlist:"Material list",bom:"BOM",rtg:"Routing",equi:"Equipment / FL",shpcost:"Shipment cost"};
const aiDocLink=(d)=>`/dashboard/sap-config?view=process&section=doc&doctype=${encodeURIComponent(d.type)}&docno=${encodeURIComponent(d.no)}`;
function TicketAiPropose({row,T}){
  const auth=useAuth();
  const token=(auth&&auth.token)||localStorage.getItem("auth_token");
  const allowed=!!(auth&&auth.hasAccess&&auth.hasAccess("solman_ai"));
  const id=String(row.ID??row.ticket_id??"").trim();
  const [data,setData]=useState(null);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState(null);
  const [openSim,setOpenSim]=useState(null);
  const [showEv,setShowEv]=useState(false);
  const [copied,setCopied]=useState(false);
  const [lang,setLang]=useState(()=>{ try{ return localStorage.getItem("solman_ai_lang")==="en"?"en":"id"; }catch(e){ return "id"; } });
  const autoRun=useRef(false);
  const API_BASE=window.__API_BASE__||"";
  const run=(force,lg)=>{
    const L=lg||lang;
    setBusy(true); setErr(null);
    fetch(`${API_BASE}/api/solman/tickets/${encodeURIComponent(id)}/ai-propose`,{method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},body:JSON.stringify({force:!!force,lang:L})})
      .then(async r=>{ const j=await r.json().catch(()=>({})); if(!r.ok||!j.ok) throw new Error(j.error||("HTTP "+r.status)); return j; })
      .then(j=>{ if((j.lang||"id")===L) setData(j); }).catch(e=>setErr(e.message)).finally(()=>setBusy(false));
  };
  useEffect(()=>{
    if(!allowed||!id) return;
    let alive=true;
    setData(null); setErr(null);
    fetch(`${API_BASE}/api/solman/tickets/${encodeURIComponent(id)}/ai-propose?lang=${lang}`,{headers:{Authorization:`Bearer ${token}`}})
      .then(r=>r.json()).then(j=>{
        if(!alive) return;
        if(j&&j.ok&&j.cached) setData(j);
        else if(autoRun.current) run(false,lang);
        autoRun.current=false;
      }).catch(()=>{});
    return ()=>{alive=false;};
  },[id,allowed,token,API_BASE,lang]); // run() sengaja tidak di deps: dipanggil sekali saat bahasa diganti
  const switchLang=(lg)=>{
    if(lg===lang||busy) return;
    autoRun.current=!!data;   // sudah ada hasil di bahasa lain → langsung buatkan versi bahasa ini
    try{ localStorage.setItem("solman_ai_lang",lg); }catch(e){}
    setLang(lg);
  };
  if(!allowed||!id) return null;
  const res=data&&data.result; const ctx=(data&&data.context)||{};
  const conf=String(res?.confidence||"").toLowerCase();
  const confC=conf==="high"?T.green:conf==="medium"?T.amber:T.red;
  const pill=(txt,c)=>(<span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:99,border:`1px solid ${c}`,color:c,whiteSpace:"nowrap"}}>{txt}</span>);
  const h=(txt)=>(<div style={{fontSize:11,color:T.textTertiary,fontWeight:700,textTransform:"uppercase",letterSpacing:0.3,marginTop:10,marginBottom:4}}>{txt}</div>);
  const btn={padding:"6px 12px",borderRadius:6,border:`1px solid ${T.border}`,background:"transparent",color:T.textSecondary,fontSize:12,fontWeight:600,cursor:busy?"wait":"pointer"};
  const simList=ctx.similar||[];
  const copyDraft=()=>{ try{navigator.clipboard.writeText(res.reply_draft||""); setCopied(true); setTimeout(()=>setCopied(false),1500);}catch(e){} };
  const ev=[...(simList.map((s,i)=>({ref:`T${i+1}`,label:`Ticket ${s.ticket_id} · ${s.status} · ${s.description||""}`}))),
    ...Object.entries(ctx.kb||{}).flatMap(([src,arr])=>(arr||[]).map(k=>({ref:k.ref,label:`${src} · ${k.title}`}))),
    ...(ctx.docs||[]).map(d=>({ref:d.ref,label:`${AI_DOC_LABEL[d.type]||d.type} ${d.no} — P01 live document check${d.error?` (not checked: ${d.error})`:""}`})),
    ...(ctx.objects||[]).map(o=>({ref:o.ref,label:`${o.object} ${o.obj_name} (${o.devclass||"-"})`})),
    ...(ctx.tcodes||[]).map(c=>({ref:c.ref,label:`T-code ${c.tcode} — ${c.text} · ${c.p01_role_count} role(s) in P01`}))];
  return (
    <div style={{border:`1px solid ${T.blue}`,borderRadius:10,padding:"10px 12px",background:"rgba(59,130,246,0.04)"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <div style={{fontSize:13,fontWeight:800,color:T.blue,flex:1}}>✨ AI · Propose Solution</div>
        <div title="Language of the proposal" style={{display:"flex",border:`1px solid ${T.border}`,borderRadius:6,overflow:"hidden"}}>
          {["id","en"].map(lg=>(<button key={lg} onClick={()=>switchLang(lg)} disabled={busy} style={{padding:"4px 10px",fontSize:11,fontWeight:700,border:"none",cursor:busy?"wait":"pointer",background:lang===lg?T.blue:"transparent",color:lang===lg?"#fff":T.textSecondary}}>{lg.toUpperCase()}</button>))}
        </div>
        {!res&&!busy&&<button onClick={()=>run(false)} style={{...btn,background:T.blue,color:"#fff",border:"none"}}>Propose solution</button>}
        {res&&!busy&&<button onClick={()=>run(true)} style={btn} title="Run the analysis again">↻ Regenerate</button>}
        {busy&&<span style={{fontSize:12,color:T.textSecondary}}>Analysing ticket, similar tickets, config &amp; workbench… (≈10–30 s)</span>}
      </div>
      {!res&&!busy&&!err&&<div style={{fontSize:12,color:T.textTertiary,marginTop:6}}>Reads this ticket, closed tickets with a similar problem, SAP config/exit KB, project documents, notes, Z-objects and t-codes mentioned. Read-only — nothing is written to SolMan.</div>}
      {err&&<div style={{fontSize:12,marginTop:8,padding:"6px 10px",borderRadius:6,background:"rgba(239,68,68,0.12)",color:"#b91c1c"}}>✕ {err}</div>}
      {res&&(
        <div style={{marginTop:8,fontSize:13,color:T.text}}>
          {data.evaluation&&(()=>{ const ev=data.evaluation; const c=ev.verdict==="correct"?T.green:ev.verdict==="partial"?T.amber:ev.verdict==="wrong"?T.red:T.textTertiary;
            return (<div style={{fontSize:12,marginBottom:8,padding:"6px 10px",borderRadius:6,border:`1px solid ${c}`}}>
              <b style={{color:c}}>Evaluated after close: {ev.verdict==="correct"?"✓ correct":ev.verdict==="partial"?"◐ partial":ev.verdict==="wrong"?"✕ wrong":"? unknown"}</b>
              {ev.score!=null&&<span style={{color:T.textTertiary}}> · score {ev.score}</span>}
              <span style={{color:T.textTertiary}}> · {String(ev.evaluated_at||"").slice(0,16)}</span>
              {ev.reason&&<div style={{marginTop:3}}>{ev.reason}</div>}
              {ev.actual_resolution&&<div style={{marginTop:3,color:T.textSecondary}}><b>Actual resolution:</b> {ev.actual_resolution}</div>}
            </div>); })()}
          {data.stale&&<div style={{fontSize:12,marginBottom:6,padding:"6px 10px",borderRadius:6,background:"rgba(245,158,11,0.12)",color:"#92400e"}}>The ticket has changed since this proposal was generated — consider Regenerate.</div>}
          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
            {pill(AI_CAT_LABEL[res.category]||res.category||"other",T.blue)}
            {pill(`Confidence: ${res.confidence||"?"}`,confC)}
          </div>
          {h("Summary")}<div style={{whiteSpace:"pre-wrap"}}>{res.summary}</div>
          {h("Likely cause")}<div style={{whiteSpace:"pre-wrap"}}>{res.likely_cause}</div>
          {Array.isArray(res.steps)&&res.steps.length>0&&(<>{h("Proposed steps")}<ol style={{margin:0,paddingLeft:20,listStyle:"decimal"}}>{res.steps.map((s,i)=><li key={i} style={{marginBottom:3}}>{s}</li>)}</ol></>)}
          {Array.isArray(res.checks)&&res.checks.length>0&&(<>{h("What to check")}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {res.checks.map((c,i)=>{ const link=AI_CHECK_LINK[c.type]; const sim=c.type==="ticket"&&simList.find(s=>String(s.ticket_id)===String(c.ref));
                const docM=(c.type==="document"||c.type==="doc")&&(ctx.docs||[]).find(d=>String(c.ref).includes(d.no));
                return (<div key={i} style={{display:"flex",gap:8,alignItems:"baseline"}}>
                  <span style={{fontSize:10,fontWeight:700,color:T.textTertiary,textTransform:"uppercase",minWidth:52}}>{c.type}</span>
                  <span style={{flex:1}}>
                    {docM?<a href={aiDocLink(docM)} target="_blank" rel="noreferrer" style={{color:T.blue,fontWeight:700}}>{c.ref}</a>
                      :link?<a href={link} target="_blank" rel="noreferrer" style={{color:T.blue,fontWeight:700}}>{c.ref}</a>
                      :sim?<button onClick={()=>setOpenSim(openSim===sim.ticket_id?null:sim.ticket_id)} style={{border:"none",background:"none",padding:0,color:T.blue,fontWeight:700,cursor:"pointer"}}>{c.ref}</button>
                      :<b>{c.ref}</b>}
                    {" — "}{c.why}{Array.isArray(c.evidence)&&c.evidence.length?<span style={{color:T.textTertiary}}> {c.evidence.map(x=>`[${String(x).replace(/[[\]]/g,"")}]`).join(" ")}</span>:null}
                  </span>
                </div>); })}
            </div></>)}
          {Array.isArray(ctx.docs)&&ctx.docs.length>0&&(<>{h(`Document check · P01 live (${ctx.docs.length})`)}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {ctx.docs.map(d=>(<div key={d.type+d.no} style={{border:`1px solid ${T.border}`,borderRadius:6,padding:"6px 8px",fontSize:12}}>
                <div style={{display:"flex",gap:8,alignItems:"baseline",flexWrap:"wrap"}}>
                  <span style={{fontSize:11,color:T.textTertiary}}>{d.ref}</span>
                  <span style={{fontWeight:700}}>{AI_DOC_LABEL[d.type]||d.type} {d.no}</span>
                  <a href={aiDocLink(d)} target="_blank" rel="noreferrer" style={{color:T.blue,fontWeight:600,fontSize:11}}>Open in Process Map → Document check ↗</a>
                </div>
                {d.error&&<div style={{color:T.textTertiary,marginTop:3}}>Not checked: {d.error}</div>}
                {(d.facts||[]).length>0&&<div style={{color:T.textSecondary,marginTop:3}}>{d.facts.map(([k,v])=>`${k}: ${v}`).join(" · ")}</div>}
                {(d.hints||[]).map((x,i)=>(<div key={i} style={{marginTop:3,color:x.lvl==="block"?"#b91c1c":x.lvl==="warn"?"#92400e":"#047857"}}>{x.lvl==="block"?"⛔":x.lvl==="warn"?"⚠️":"✓"} {lang==="id"&&x.id?x.id:x.en}</div>))}
              </div>))}
            </div></>)}
          {simList.length>0&&(<>{h(`Similar closed tickets (${simList.length})`)}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {simList.map((s,i)=>(<div key={s.ticket_id} style={{border:`1px solid ${T.border}`,borderRadius:6,padding:"6px 8px"}}>
                <div onClick={()=>setOpenSim(openSim===s.ticket_id?null:s.ticket_id)} style={{cursor:"pointer",display:"flex",gap:8,alignItems:"baseline"}}>
                  <span style={{fontSize:11,color:T.textTertiary}}>T{i+1}</span>
                  <span style={{fontWeight:700,color:T.blue}}>{s.ticket_id}</span>
                  <span style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.description}</span>
                  <span style={{fontSize:11,color:T.textTertiary,whiteSpace:"nowrap"}}>{s.status} · {s.created_on}</span>
                  <span style={{fontSize:11,color:T.textTertiary}}>{openSim===s.ticket_id?"▲":"▼"}</span>
                </div>
                {openSim===s.ticket_id&&(<div style={{fontSize:12,marginTop:6,display:"grid",gap:6}}>
                  <div><b>Problem</b><div style={{whiteSpace:"pre-wrap"}}>{s.problem||"—"}</div></div>
                  <div><b>Resolution / replies</b><div style={{whiteSpace:"pre-wrap"}}>{s.resolution||"—"}</div></div>
                </div>)}
              </div>))}
            </div></>)}
          {Array.isArray(res.missing_info)&&res.missing_info.length>0&&(<>{h("Missing information")}<ul style={{margin:0,paddingLeft:20,listStyle:"disc"}}>{res.missing_info.map((s,i)=><li key={i}>{s}</li>)}</ul></>)}
          {res.reply_draft&&(<>{h("Reply draft (paste into SolMan)")}
            <div style={{position:"relative",border:`1px solid ${T.border}`,borderRadius:6,padding:"8px 10px",whiteSpace:"pre-wrap",background:T.card}}>
              {res.reply_draft}
              <button onClick={copyDraft} style={{...btn,position:"absolute",top:6,right:6,padding:"2px 8px",fontSize:11,background:T.card}}>{copied?"Copied ✓":"Copy"}</button>
            </div></>)}
          {ev.length>0&&(<div style={{marginTop:10}}>
            <button onClick={()=>setShowEv(s=>!s)} style={{...btn,padding:"2px 8px",fontSize:11}}>{showEv?"Hide":"Show"} evidence ({ev.length})</button>
            {showEv&&<div style={{fontSize:11,color:T.textSecondary,marginTop:6,display:"grid",gap:2}}>{ev.map(e=><div key={e.ref}><b>[{e.ref}]</b> {e.label}</div>)}</div>}
          </div>)}
          <div style={{fontSize:11,color:T.textTertiary,marginTop:10}}>
            AI-generated proposal — verify before acting. {data.model} · {data.ms?Math.round(data.ms/100)/10+" s":""} · by {data.created_by} · {String(data.created_at||"").replace("T"," ").slice(0,16)}{data.cached?" · cached":""}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ATTACHMENTS — lampiran incident ticket (FM Z_SOLMAN_TICKET_ATTACH, SM1)
   Backend: backend/solmanAttach.js · izin 'solman' · read-only
═══════════════════════════════════════════════════════════════ */
const ATT_SHEET_RX=/\.(xlsx|xlsm|xls|csv|txt|log|pdf|jpe?g|png|bmp|tiff?|gif|docx?|pptx?|rtf|odt|odp)$/i;
const ATT_OCR_RX=/\.(pdf|jpe?g|png|bmp|tiff?|gif|docx?|pptx?|rtf|odt|odp)$/i;
const ATT_INLINE_RX=/\.(pdf|png|jpe?g|gif|bmp|txt)$/i;
const attIcon=(n)=>/\.(xlsx|xlsm|xls|csv)$/i.test(n)?"📊":/\.pdf$/i.test(n)?"📕":/\.(png|jpe?g|gif|bmp)$/i.test(n)?"🖼️":/\.(docx?|rtf)$/i.test(n)?"📝":"📎";
const fmtBytes=(b)=>!b?"":b<1024?b+" B":b<1048576?(b/1024).toFixed(1)+" KB":(b/1048576).toFixed(1)+" MB";
function TicketAttachments({row,T}){
  const auth=useAuth();
  const token=(auth&&auth.token)||localStorage.getItem("auth_token");
  const id=String(row.ID??row.ticket_id??"").trim();
  const API_BASE=window.__API_BASE__||"";
  const [list,setList]=useState(null);
  const [err,setErr]=useState(null);
  const [busy,setBusy]=useState(false);
  const [prev,setPrev]=useState(null);      // {phio, data|null, err, sheet}
  const [dl,setDl]=useState(null);
  const load=(fresh)=>{
    if(!id) return;
    setBusy(true); setErr(null);
    fetch(`${API_BASE}/api/solman/tickets/${encodeURIComponent(id)}/attachments${fresh?"?fresh=1":""}`,{headers:{Authorization:`Bearer ${token}`}})
      .then(async r=>{ const j=await r.json().catch(()=>({})); if(!r.ok||!j.ok) throw new Error(j.error||("HTTP "+r.status)); return j; })
      .then(j=>setList(j)).catch(e=>setErr(e.message)).finally(()=>setBusy(false));
  };
  useEffect(()=>{ setList(null); setPrev(null); load(false); },[id]); // load() sengaja tidak di deps
  if(!id) return null;
  const fileUrl=(a,inline)=>`${API_BASE}/api/solman/tickets/${encodeURIComponent(id)}/attachments/${a.phio_id}${inline?"?inline=1":""}`;
  const getBlob=async(a,inline)=>{ const r=await fetch(fileUrl(a,inline),{headers:{Authorization:`Bearer ${token}`}}); if(!r.ok){ const j=await r.json().catch(()=>({})); throw new Error(j.error||("HTTP "+r.status)); } return r.blob(); };
  const download=async(a)=>{ setDl(a.phio_id); try{ const b=await getBlob(a,false); const u=URL.createObjectURL(b); const el=document.createElement("a"); el.href=u; el.download=a.file_name||a.phio_id; document.body.appendChild(el); el.click(); el.remove(); setTimeout(()=>URL.revokeObjectURL(u),5000); }catch(e){ setErr(e.message); } finally{ setDl(null); } };
  const openInline=async(a)=>{ setDl(a.phio_id); const w=window.open("","_blank"); try{ const b=await getBlob(a,true); const u=URL.createObjectURL(b); if(w) w.location.href=u; }catch(e){ if(w) w.close(); setErr(e.message); } finally{ setDl(null); } };
  const preview=(a)=>{
    if(prev&&prev.phio===a.phio_id){ setPrev(null); return; }
    setPrev({phio:a.phio_id,data:null,err:null,sheet:0});
    fetch(`${fileUrl(a,false)}/preview?rows=200`,{headers:{Authorization:`Bearer ${token}`}})
      .then(async r=>{ const j=await r.json().catch(()=>({})); if(!r.ok||!j.ok) throw new Error(j.error||("HTTP "+r.status)); return j; })
      .then(j=>setPrev(p=>p&&p.phio===a.phio_id?{...p,data:j}:p)).catch(e=>setPrev(p=>p&&p.phio===a.phio_id?{...p,err:e.message}:p));
  };
  const btn={padding:"3px 9px",borderRadius:6,border:`1px solid ${T.border}`,background:"transparent",color:T.textSecondary,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"};
  const items=(list&&list.items)||[];
  return (
    <div style={{border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 12px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <div style={{fontSize:13,fontWeight:800,color:T.text,flex:1}}>📎 Attachments{list?` (${items.length})`:""}</div>
        <button onClick={()=>load(true)} disabled={busy} style={{...btn,cursor:busy?"wait":"pointer"}} title="Re-read the attachment list from SolMan">↻ Refresh</button>
      </div>
      {busy&&!list&&<div style={{fontSize:12,color:T.textTertiary,marginTop:6}}>Reading attachments from SolMan…</div>}
      {err&&<div style={{fontSize:12,marginTop:6,padding:"6px 10px",borderRadius:6,background:"rgba(239,68,68,0.12)",color:"#b91c1c"}}>✕ {err}</div>}
      {list&&!items.length&&<div style={{fontSize:12,color:T.textTertiary,marginTop:6}}>No attachments on this ticket.</div>}
      {items.length>0&&(
        <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8}}>
          {items.map(a=>(
            <div key={a.phio_id} style={{border:`1px solid ${T.border}`,borderRadius:8,padding:"6px 8px"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{fontSize:16}}>{attIcon(a.file_name||"")}</span>
                <span style={{flex:1,minWidth:160,fontSize:13,fontWeight:600,color:T.text,wordBreak:"break-word"}}>{a.file_name}</span>
                <span style={{fontSize:11,color:T.textTertiary,whiteSpace:"nowrap"}}>{a.created_by} · {a.created_at}{a.size?` · ${fmtBytes(a.size)}`:""}</span>
                {ATT_OCR_RX.test(a.file_name||"")&&a.text_status&&<span title={a.text_error||(a.text_method?`Text read via ${a.text_method}`:"")} style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:99,border:`1px solid ${a.text_status==="done"?T.green:a.text_status==="error"?T.red:T.amber}`,color:a.text_status==="done"?T.green:a.text_status==="error"?T.red:T.amber}}>{a.text_status==="done"?`text ✓ ${a.text_chars||0} chars`:a.text_status==="error"?"text ✕":"reading text…"}</span>}
                {ATT_SHEET_RX.test(a.file_name||"")&&<button onClick={()=>preview(a)} style={btn}>{prev&&prev.phio===a.phio_id?"Hide preview":"Preview"}</button>}
                {ATT_INLINE_RX.test(a.file_name||"")&&!/\.txt$/i.test(a.file_name||"")&&<button onClick={()=>openInline(a)} disabled={dl===a.phio_id} style={btn}>Open</button>}
                <button onClick={()=>download(a)} disabled={dl===a.phio_id} style={btn}>{dl===a.phio_id?"…":"Download"}</button>
              </div>
              {prev&&prev.phio===a.phio_id&&(
                <div style={{marginTop:8}}>
                  {!prev.data&&!prev.err&&<div style={{fontSize:12,color:T.textTertiary}}>Loading preview…{ATT_OCR_RX.test(a.file_name||"")?" (reading text / OCR can take up to a minute)":""}</div>}
                  {prev.err&&<div style={{fontSize:12,color:"#b91c1c"}}>✕ {prev.err}</div>}
                  {prev.data&&typeof prev.data.text==="string"&&(
                    <div>
                      <div style={{fontSize:11,color:T.textTertiary,marginBottom:4}}>Text extracted from the file (PDF text layer / OCR) — this is what the AI reads.</div>
                      <pre style={{margin:0,maxHeight:320,overflow:"auto",whiteSpace:"pre-wrap",fontSize:11,border:`1px solid ${T.border}`,borderRadius:6,padding:8,background:T.card}}>{prev.data.text||"(no text found)"}</pre>
                    </div>)}
                  {prev.data&&prev.data.previewable===false&&<div style={{fontSize:12,color:T.textTertiary}}>Preview not available for this file type.</div>}
                  {prev.data&&prev.data.sheets&&(()=>{ const sh=prev.data.sheets[prev.sheet]||prev.data.sheets[0]; const rows=(sh&&sh.rows)||[]; const width=Math.max(0,...rows.map(r=>r.length));
                    return (<>
                      {prev.data.sheets.length>1&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:6}}>{prev.data.sheets.map((s,i)=>(
                        <button key={s.name} onClick={()=>setPrev(p=>({...p,sheet:i}))} style={{...btn,background:i===prev.sheet?T.blue:"transparent",color:i===prev.sheet?"#fff":T.textSecondary,border:i===prev.sheet?"none":btn.border}}>{s.name} ({Math.max(0,s.total_rows-1)})</button>))}</div>}
                      <div style={{overflow:"auto",maxHeight:320,border:`1px solid ${T.border}`,borderRadius:6}}>
                        <table style={{borderCollapse:"collapse",fontSize:11,whiteSpace:"nowrap"}}>
                          <tbody>{rows.map((r,ri)=>(
                            <tr key={ri} style={{background:ri===0?"rgba(59,130,246,0.08)":ri%2?"#FBFDFF":"transparent"}}>
                              <td style={{padding:"3px 6px",color:T.textTertiary,borderRight:`1px solid ${T.border}`,position:"sticky",left:0,background:T.card}}>{ri===0?"":ri}</td>
                              {Array.from({length:width}).map((_,ci)=>{ const v=r[ci]; const empty=ri>0&&String(v??"").trim()==="";
                                return <td key={ci} style={{padding:"3px 8px",borderBottom:`1px solid ${T.border}`,fontWeight:ri===0?700:400,background:empty?"rgba(245,158,11,0.10)":undefined}}>{String(v??"")}</td>; })}
                            </tr>))}</tbody>
                        </table>
                      </div>
                      <div style={{fontSize:11,color:T.textTertiary,marginTop:4}}>{sh?`${Math.max(0,sh.total_rows-1)} data row(s)`:""}{sh&&sh.total_rows>rows.length?` · showing first ${rows.length-1}`:""} · empty cells highlighted</div>
                    </>); })()}
                </div>)}
            </div>))}
        </div>)}
    </div>
  );
}

function TicketDetailModal({row,onClose,T,z=1000,onPrev,onNext,pos}){
  if(!row) return null;
  const fmt=v=>{ if(v===null||v===undefined||v==="") return "—"; if(v instanceof Date) return isNaN(v)?"—":v.toLocaleDateString("en-GB"); if(typeof v==="object") return null; return String(v); };
  const keys=[...EXPORT_HEADERS.filter(h=>h in row), ...Object.keys(row).filter(k=>!EXPORT_HEADERS.includes(k)&&!k.startsWith("_"))].filter(k=>fmt(row[k])!==null);
  const wide=k=>/description|text|note|summary/i.test(k)||String(fmt(row[k])).length>60;
  const copy=()=>{ const txt=keys.map(k=>k+": "+fmt(row[k])).join("\n"); try{navigator.clipboard.writeText(txt);}catch(e){} };
  return (
    <div ref={el=>el&&!el.contains(document.activeElement)&&el.focus()} tabIndex={-1} onKeyDown={e=>{if(e.key==="Escape")onClose();else if(e.key==="ArrowLeft"&&onPrev)onPrev();else if(e.key==="ArrowRight"&&onNext)onNext();}} onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.45)",zIndex:z,display:"flex",justifyContent:"center",alignItems:"center",padding:16,outline:"none"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"min(860px,100%)",maxHeight:"90vh",background:T.card,borderRadius:12,boxShadow:"0 20px 50px rgba(0,0,0,0.25)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"flex-start",gap:12}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,color:T.textTertiary,fontWeight:600,letterSpacing:0.4}}>TICKET DETAIL</div>
            <div style={{fontSize:18,fontWeight:700,color:T.blue}}>{fmt(row.ID)}</div>
            <div style={{fontSize:13,color:T.text,marginTop:2,wordBreak:"break-word"}}>{fmt(row.Description)}</div>
          </div>
          {(onPrev!==undefined||onNext!==undefined)&&(<div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}><button onClick={()=>onPrev&&onPrev()} disabled={!onPrev} title="Previous ticket (←)" style={{padding:"6px 10px",borderRadius:6,border:`1px solid ${T.border}`,background:"transparent",color:T.textSecondary,fontSize:12,fontWeight:700,whiteSpace:"nowrap",opacity:onPrev?1:0.35,cursor:onPrev?"pointer":"default"}}>‹ Prev</button>{pos&&<span style={{fontSize:11,color:T.textSecondary,fontWeight:600,whiteSpace:"nowrap"}}>{pos}</span>}<button onClick={()=>onNext&&onNext()} disabled={!onNext} title="Next ticket (→)" style={{padding:"6px 10px",borderRadius:6,border:`1px solid ${T.border}`,background:"transparent",color:T.textSecondary,fontSize:12,fontWeight:700,whiteSpace:"nowrap",opacity:onNext?1:0.35,cursor:onNext?"pointer":"default"}}>Next ›</button></div>)}
          <button onClick={copy} title="Copy all fields" style={{padding:"6px 10px",borderRadius:6,border:`1px solid ${T.border}`,background:"transparent",color:T.textSecondary,cursor:"pointer",fontSize:12}}>Copy</button>
          <button onClick={onClose} title="Close (Esc)" style={{padding:"6px 10px",borderRadius:6,border:`1px solid ${T.border}`,background:"transparent",color:T.textSecondary,cursor:"pointer",fontSize:12}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"12px 20px 20px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 16px",alignContent:"start"}}>
          <div style={{gridColumn:"1 / -1"}}><TicketAiPropose key={row.ID} row={row} T={T}/></div>
          {keys.map(k=>(
            <div key={k} style={{gridColumn:wide(k)?"1 / -1":"auto",minWidth:0}}>
              <div style={{fontSize:11,color:T.textTertiary,fontWeight:600,textTransform:"uppercase",letterSpacing:0.3}}>{k}</div>
              <div style={{fontSize:13,color:T.text,wordBreak:"break-word",whiteSpace:"pre-wrap"}}>{fmt(row[k])}</div>
            </div>
          ))}
          <div style={{gridColumn:"1 / -1"}}><TicketAttachments key={row.ID} row={row} T={T}/></div>
          <div style={{gridColumn:"1 / -1"}}><TicketStatusAction key={row.ID} row={row} T={T}/></div>
          <div style={{gridColumn:"1 / -1"}}><TicketTexts id={row.ID} T={T}/></div>
        </div>
      </div>
    </div>
  );
}

function ExportPanel({data, meta, T, t, defaultCompany, defaultYear}){
  const companies = meta.companies || [];
  const years     = meta.years || [];
  const [company,setCompany] = useState(
    defaultCompany && defaultCompany!=="All" ? defaultCompany : (companies[0]||"")
  );
  const [year,setYear] = useState(
    (Array.isArray(defaultYear) && defaultYear.length===1) ? Number(defaultYear[0]) : (years[years.length-1]||new Date().getFullYear())
  );
  const [month,setMonth] = useState(new Date().getMonth()); // 0-based
  const [busy,setBusy]   = useState(false);
  const [done,setDone]   = useState(null); // {filename,count} setelah export sukses
  const [spBusy,setSpBusy] = useState(false);
  const [spResults,setSpResults] = useState(null); // [{company,ok,msg}]

  const matched = useMemo(()=>{
    if(!data||!company) return [];
    return data.filter(r=> r._year===year && r._month===month && (company==="All" ? true : r.Company===company));
  },[data,company,year,month]);

  // Bangun 1 workbook (styling header + format tanggal persis file contoh) → {wb, filename}
  const buildWb = useCallback((companyName, list)=>{
    const mm  = MONTH_NUM[month];
    const mmm = MONTH_FULL[month];
    const rows = list.map(r=>{
      const out = {};
      EXPORT_HEADERS.forEach(h=>{
        let v = r[h];
        if(h==="Created On2"){
          const d = parseAnyDate(v);
          out[h] = d || v || "";
        }else if(EXPORT_NUMERIC_COLS.has(h)){
          const n = Number(v);
          out[h] = (v!==""&&v!=null&&Number.isFinite(n)) ? n : (v||"");
        }else{
          out[h] = v ?? "";
        }
      });
      return out;
    });
    // cellDates WAJIB: tanpa ini json_to_sheet mengonversi Date -> angka serial
    // lebih dulu, sehingga pengecekan "instanceof Date" di bawah tidak pernah
    // kena dan kolom Created On2 keluar dengan format m/d/yy, bukan d-mmm-yyyy
    // seperti file contoh. (Sama seperti perbaikan yang sudah diterapkan di
    // export tab Detail Tiket.)
    const ws = XLSX.utils.json_to_sheet(rows,{header:EXPORT_HEADERS,cellDates:true});
    ws['!cols'] = EXPORT_HEADERS.map(h=>({wch:EXPORT_COL_WIDTHS[h]||14}));
    const range = XLSX.utils.decode_range(ws['!ref']);
    // Header: bold + fill hijau (#4EA72E) + teks putih, font Aptos Narrow 11 — sama spt file contoh
    const HEADER_STYLE = {
      font: { name:"Aptos Narrow", sz:11, bold:true, color:{rgb:"FFFFFFFF"} },
      fill: { patternType:"solid", fgColor:{rgb:"FF4EA72E"} },
    };
    for(let C=range.s.c;C<=range.e.c;C++){
      const ref = XLSX.utils.encode_cell({r:range.s.r,c:C});
      if(ws[ref]) ws[ref].s = HEADER_STYLE;
    }
    // Format kolom "Created On2" sbg tanggal asli (spt file contoh: 9-Apr-2026)
    const col2 = EXPORT_HEADERS.indexOf("Created On2");
    for(let R=range.s.r+1;R<=range.e.r;R++){
      const ref = XLSX.utils.encode_cell({r:R,c:col2});
      if(ws[ref] && ws[ref].v instanceof Date){ ws[ref].t='d'; ws[ref].z='[$-409]d\\-mmm\\-yyyy;@'; delete ws[ref].w; }
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, String(companyName).slice(0,31)||"Sheet1");
    // Pola nama file DISALIN PERSIS dari file bulanan yang sudah ada di folder
    // (contoh: "01. Solman tickets January 2026 - OSI.xlsx"):
    //   <MM><titik><spasi>Solman tickets <BulanPenuh> <tahun><spasi>-<spasi><company>
    // Perhatikan dua hal yang dulu salah: setelah angka bulan adalah ". "
    // (titik + spasi, bukan "-"), dan sebelum nama company adalah " - "
    // (spasi di KEDUA sisi tanda hubung).
    const filename = `${mm}. Solman tickets ${mmm} ${year} - ${companyName}.xlsx`;
    return { wb, filename };
  },[month,year]);

  // Group matched → { companyName: rows[] } (untuk mode "All" = per company)
  const groupByCompany = useCallback(()=>{
    if(company!=="All") return [[company, matched]];
    const groups = {};
    matched.forEach(r=>{ const c = r.Company || "UNKNOWN"; (groups[c] || (groups[c]=[])).push(r); });
    return Object.keys(groups).sort().map(c=>[c, groups[c]]);
  },[company,matched]);

  const doExport = useCallback(()=>{
    if(!matched.length||busy) return;
    setBusy(true); setDone(null);
    try{
      const parts = groupByCompany();
      const names = [];
      parts.forEach(([c,list])=>{ const {wb,filename}=buildWb(c,list); XLSX.writeFile(wb,filename); names.push(filename); });
      if(names.length>1) setDone({ filename:`${names.length} file (${parts.map(p=>p[0]).join(", ")})`, count:matched.length });
      else setDone({ filename:names[0], count:matched.length });
    } finally { setBusy(false); }
  },[matched,busy,groupByCompany,buildWb]);

  // Upload ke SharePoint via backend (Microsoft Graph). Mode "All" → 1 upload per company.
  const doUploadSp = useCallback(async ()=>{
    if(!matched.length||spBusy) return;
    setSpBusy(true); setSpResults(null);
    // Ambil token (pola sama dgn dataLoader)
    const lsKeys=["auth_token","token","authToken","jwt","access_token"];
    let token=new URLSearchParams(window.location.search).get("token");
    if(!token){ for(const k of lsKeys){ const v=localStorage.getItem(k); if(v&&v.length>20){token=v;break;} } }
    if(!token){ for(const k of lsKeys){ const v=sessionStorage.getItem(k); if(v&&v.length>20){token=v;break;} } }
    const API_BASE=window.__API_BASE__||"";
    const results=[];
    try{
      for(const [c,list] of groupByCompany()){
        const {wb,filename}=buildWb(c,list);
        const dataBase64=XLSX.write(wb,{type:"base64",bookType:"xlsx"});
        try{
          const res=await fetch(`${API_BASE}/api/solman/sharepoint-upload`,{
            method:"POST",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
            body:JSON.stringify({company:c,year,filename,dataBase64}),
          });
          const j=await res.json().catch(()=>({}));
          results.push({company:c, ok:res.ok, msg: res.ok ? (j.folder||filename) : (j.error||`HTTP ${res.status}`)});
        }catch(e){ results.push({company:c, ok:false, msg:String(e.message||e)}); }
      }
    } finally {
      setSpResults(results);
      setSpBusy(false);
    }
  },[matched,spBusy,groupByCompany,buildWb,year]);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20,maxWidth:640}}>
      <Card title={t('solm.export.title')} subtitle={t('solm.export.subtitle')}>
        <div style={{display:"flex",flexDirection:"column",gap:14,padding:"4px 2px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
            <label style={{display:"flex",flexDirection:"column",gap:5}}>
              <span style={{fontSize:11,fontWeight:700,color:T.textTertiary,textTransform:"uppercase",letterSpacing:"0.4px"}}>{t('solm.export.company')}</span>
              <select value={company} onChange={e=>setCompany(e.target.value)}
                style={{padding:"8px 10px",borderRadius:8,border:`1px solid ${T.border}`,fontSize:13,fontWeight:600,color:T.text,background:T.card}}>
                {companies.length===0 && <option value="">—</option>}
                {companies.length>1 && <option value="All">{t('solm.allCompanies')}</option>}
                {companies.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label style={{display:"flex",flexDirection:"column",gap:5}}>
              <span style={{fontSize:11,fontWeight:700,color:T.textTertiary,textTransform:"uppercase",letterSpacing:"0.4px"}}>{t('solm.export.month')}</span>
              <select value={month} onChange={e=>setMonth(Number(e.target.value))}
                style={{padding:"8px 10px",borderRadius:8,border:`1px solid ${T.border}`,fontSize:13,fontWeight:600,color:T.text,background:T.card}}>
                {MONTH_FULL.map((m,i)=><option key={m} value={i}>{m}</option>)}
              </select>
            </label>
            <label style={{display:"flex",flexDirection:"column",gap:5}}>
              <span style={{fontSize:11,fontWeight:700,color:T.textTertiary,textTransform:"uppercase",letterSpacing:"0.4px"}}>{t('solm.export.year')}</span>
              <select value={year} onChange={e=>setYear(Number(e.target.value))}
                style={{padding:"8px 10px",borderRadius:8,border:`1px solid ${T.border}`,fontSize:13,fontWeight:600,color:T.text,background:T.card}}>
                {years.length===0 && <option value={year}>{year}</option>}
                {years.map(y=><option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          </div>

          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:10,background:T.bg,border:`1px solid ${T.border}`}}>
            <span style={{fontSize:13,color:T.textSecondary}}>
              {t('solm.export.matchCount')} <strong style={{color:T.text}}>{matched.length.toLocaleString()}</strong> {t('solm.tickets')}
            </span>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <button onClick={doExport} disabled={!matched.length||busy}
                style={{display:"flex",alignItems:"center",gap:6,padding:"9px 18px",borderRadius:8,border:"none",
                  background:(!matched.length||busy)?T.textTertiary:T.blue,color:"#fff",fontSize:13,fontWeight:700,
                  cursor:(!matched.length||busy)?"not-allowed":"pointer"}}>
                <i className="ti ti-file-export" style={{fontSize:15}}/> {busy?t('solm.export.exporting'):t('solm.export.button')}
              </button>
              <button onClick={doUploadSp} disabled={!matched.length||spBusy}
                style={{display:"flex",alignItems:"center",gap:6,padding:"9px 18px",borderRadius:8,border:`1px solid ${T.border}`,
                  background:(!matched.length||spBusy)?T.bg:T.card,color:(!matched.length||spBusy)?T.textTertiary:T.text,fontSize:13,fontWeight:700,
                  cursor:(!matched.length||spBusy)?"not-allowed":"pointer"}}>
                <i className="ti ti-cloud-upload" style={{fontSize:15}}/> {spBusy?t('solm.export.spUploading'):t('solm.export.spButton')}
              </button>
            </div>
          </div>

          {done && (
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",borderRadius:10,background:T.greenBg,border:"1px solid #BBF7D0",fontSize:12.5,color:T.green}}>
              <i className="ti ti-circle-check" style={{fontSize:16}}/>
              {t('solm.export.done')} <code style={{background:"#fff",padding:"2px 6px",borderRadius:5,fontSize:11.5}}>{done.filename}</code> ({done.count} {t('solm.tickets')})
            </div>
          )}

          {spResults && (
            <div style={{display:"flex",flexDirection:"column",gap:6,padding:"10px 14px",borderRadius:10,background:T.bg,border:`1px solid ${T.border}`,fontSize:12.5}}>
              <span style={{fontWeight:700,color:T.text}}>{t('solm.export.spDone')}</span>
              {spResults.map((r,i)=>(
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,color:r.ok?T.green:T.red}}>
                  <i className={r.ok?"ti ti-circle-check":"ti ti-circle-x"} style={{fontSize:15,marginTop:1}}/>
                  <span><strong>{r.company}</strong>{r.ok?" → ":": "}{r.msg}</span>
                </div>
              ))}
            </div>
          )}

          <p style={{fontSize:11.5,color:T.textTertiary,lineHeight:1.5,margin:0}}>
            {t('solm.export.note')}
          </p>
        </div>
      </Card>
    </div>
  );
}

function useSolmanData(dataLoader){
  const embeddedData=typeof window!=="undefined"&&window.__SOLMAN_DATA__
    ?hydrateRows(window.__SOLMAN_DATA__):null;
  const embeddedFile=typeof window!=="undefined"&&window.__SOLMAN_FILE__
    ?window.__SOLMAN_FILE__:"";

  const[data,setData]          =useState(embeddedData);
  const[fileName,setFileName]  =useState(embeddedFile);
  const[filterCompany,setFC]   =useState("All");
  const[filterTeam,setFT]      =useState("All");
  const[filterYear,setFY]      =useState([]);
  const[filterStatus,setFS]    =useState("All");
  const[activeTab,setActiveTab]=useState("overview");
  const[exporting,setExporting]=useState(false);
  const[dbLoadStatus,setDbLoadStatus]=useState(null); // null | "loading" | "loaded" | "error" | "empty"

  /* ── Hydrate a DB row → dashboard row format ── */
  const hydrateDbRow=useCallback(row=>({
    // Map DB column names back to original CSV header names
    "ID":            row.ticket_id||"",
    "Created By":    row.created_by||"",
    "Company":       row.company||"",
    "Description":   row.description||"",
    "Priority":      row.priority||"",
    "Created On":    row.created_on||"",
    "Created On2":   row.created_on2||"",
    "Status":        row.status||"",
    "Reporter":      row.reporter||"",
    "Impact":        row.impact||"",
    "Processor":     row.processor||"",
    "Service Team":  row.service_team||"",
    "System ID":     row.system_id||"",
    "System Status": row.system_status||"",
    "Transaction Type":    row.transaction_type||"",
    "Client":              row.client||"",
    "Transaction Type ID": row.transaction_type_id||"",
    "Status ID":           row.status_id||"",
    "Trans. Category":     row.trans_category||"",
    "System":              row.system||"",
    "SAP Installation":    row.sap_installation||"",
    "Software Release":    row.software_release||"",
    "SAP Component":       row.sap_component||"",
    // Derived fields
    _year:       row.year_num||null,
    _month:      row.month_num?row.month_num-1:null,  // 0-based
    _monthKey:   row.month_year||null,
    _monthLabel: row.month_year
      ? `${MONTH_ABBR[(row.month_num||1)-1]} ${row.year_num}`
      : null,
    _date: row.month_year
      ? new Date(row.year_num, (row.month_num||1)-1, 1)
      : null,
  }),[]);

  /* ── Load all tickets from DB via API ── */
  const loadFromDB=useCallback(async()=>{
    // Mode share (publik): ambil tiket via dataLoader, tanpa token/login.
    if(dataLoader){
      setDbLoadStatus("loading");
      try{
        const json=await dataLoader();
        const allRows=(json&&json.tickets)||[];
        if(allRows.length===0){ setDbLoadStatus("empty"); return; }
        const rows=allRows.map(hydrateDbRow);
        setData(rows);
        setFileName(`Database (${allRows.length.toLocaleString()} tiket)`);
        setDbLoadStatus("loaded");
      }catch(e){ setDbLoadStatus("error"); }
      return;
    }
    // Get token
    const lsKeys=["auth_token","token","authToken","jwt","access_token"];
    let token=new URLSearchParams(window.location.search).get("token");
    if(!token){ for(const k of lsKeys){ const v=localStorage.getItem(k); if(v&&v.length>20){token=v;break;} } }
    if(!token){ for(const k of lsKeys){ const v=sessionStorage.getItem(k); if(v&&v.length>20){token=v;break;} } }
    if(!token){ setDbLoadStatus("error"); return; }

    setDbLoadStatus("loading");
    try{
      const API_BASE=window.__API_BASE__||"";
      // Load in batches of 1000 to handle large datasets
      let allRows=[], offset=0, total=null;
      do{
        const res=await fetch(`${API_BASE}/api/tickets?limit=1000&offset=${offset}`,{
          headers:{"Authorization":`Bearer ${token}`}
        });
        if(!res.ok){ setDbLoadStatus("error"); return; }
        const json=await res.json();
        total=json.total;
        allRows=[...allRows,...json.tickets];
        offset+=1000;
      }while(allRows.length<total);

      if(allRows.length===0){ setDbLoadStatus("empty"); return; }

      const rows=allRows.map(hydrateDbRow);
      setData(rows);
      setFileName(`Database (${allRows.length.toLocaleString()} tiket)`);
      setDbLoadStatus("loaded");
    }catch(e){
      setDbLoadStatus("error");
    }
  },[hydrateDbRow,dataLoader]);

  /* ── Auto-load from DB on mount (only if no embedded/CSV data) ── */
  useEffect(()=>{
    if(!embeddedData){
      loadFromDB();
    }
  // eslint-disable-next-line
  },[]);

  const resetAll=useCallback(()=>{
    setData(null);setFileName("");
    setFC("All");setFT("All");setFY("All");setFS("All");
  },[]);

  const setFilter=useCallback(({company,team,year,status}={})=>{
    if(company!==undefined)setFC(company);
    if(team!==undefined)   setFT(team);
    if(year!==undefined)   setFY(String(year));
    if(status!==undefined) setFS(status);
  },[]);

  const meta=useMemo(()=>{
    if(!data)return{};
    return{
      companies:[...new Set(data.map(r=>r.Company).filter(Boolean))].sort(),
      teams:    [...new Set(data.map(r=>r.Impact).filter(Boolean))].sort(),
      years:    [...new Set(data.map(r=>r._year).filter(Boolean))].sort(),
      statuses: [...new Set(data.map(r=>r.Status).filter(Boolean))].sort(),
    };
  },[data]);

  // Default: saat halaman pertama dibuka, filter ke tahun terakhir (sekali saja)
  const yearInitRef=useRef(false);
  useEffect(()=>{
    if(yearInitRef.current) return;
    const ys=meta.years||[];
    if(!ys.length) return;
    yearInitRef.current=true;
    setFY(prev=>prev.length?prev:[String(ys[ys.length-1])]);
  },[meta.years]);

  const filtered=useMemo(()=>{
    if(!data)return[];
    return data.filter(r=>
      (filterCompany==="All"||r.Company===filterCompany)&&
      (filterTeam==="All"   ||r.Impact===filterTeam)&&
      (filterYear.length===0   ||filterYear.includes(String(r._year)))&&
      (filterStatus==="All" ||r.Status===filterStatus||(filterStatus==="__OPEN__"&&OPEN_STATUSES.includes(r.Status)))
    );
  },[data,filterCompany,filterTeam,filterYear,filterStatus]);

  const stats=useMemo(()=>{
    if(!filtered.length)return null;
    const total    =filtered.length;
    const open     =filtered.filter(r=>OPEN_STATUSES.includes(r.Status)).length;
    const closed   =filtered.filter(r=>r.Status==="Closed").length;
    const withdrawn=filtered.filter(r=>r.Status==="Withdrawn").length;

    const byStatus  =Object.entries(countBy(filtered,"Status")).sort((a,b)=>b[1]-a[1]);
    const byCompany =Object.entries(countBy(filtered,"Company")).sort((a,b)=>b[1]-a[1])
      .map(([name,value],i)=>({name,value,fill:COMPANY_PALETTE[i%COMPANY_PALETTE.length]}));
    const byTeam    =Object.entries(countBy(filtered,"Impact")).sort((a,b)=>b[1]-a[1])
      .map(([name,value])=>({name,value}));
    const byPriority=Object.entries(countBy(filtered,"Priority")).sort((a,b)=>a[0].localeCompare(b[0]))
      .map(([name,value])=>({name,value}));

    const mStatusMap={};
    filtered.forEach(r=>{
      if(!r._monthKey)return;
      const k=r._monthKey;
      if(!mStatusMap[k]){mStatusMap[k]={key:k,label:r._monthLabel,date:r._date,total:0};STATUS_LIST.forEach(s=>{mStatusMap[k][s]=0;});}
      mStatusMap[k].total++;
      if(mStatusMap[k][r.Status]!==undefined)mStatusMap[k][r.Status]++;
    });
    const byMonthStatus=Object.values(mStatusMap).sort((a,b)=>a.date-b.date);
    const byMonth=byMonthStatus.map(r=>({...r,open:OPEN_STATUSES.reduce((s,st)=>s+(r[st]||0),0)}));

    const companies=[...new Set(filtered.map(r=>r.Company).filter(Boolean))].sort();
    const byCompanyStatus=companies.map(co=>{
      const tr=filtered.filter(r=>r.Company===co);
      const entry={company:co,total:tr.length};
      STATUS_LIST.forEach(s=>{entry[s]=tr.filter(r=>r.Status===s).length;});
      return entry;
    }).sort((a,b)=>b.total-a.total);

    const teamNames=[...new Set(filtered.map(r=>r.Impact).filter(Boolean))].sort();
    const byTeamStatus=teamNames.map(team=>{
      const tr=filtered.filter(r=>r.Impact===team);
      const entry={team,_total:tr.length};
      STATUS_LIST.forEach(s=>{entry[s]=tr.filter(r=>r.Status===s).length;});
      return entry;
    }).sort((a,b)=>b._total-a._total);

    const priorities=[...new Set(filtered.map(r=>r.Priority).filter(Boolean))].sort();
    const byPriorityStatus=priorities.map(pr=>{
      const tr=filtered.filter(r=>r.Priority===pr);
      const entry={priority:pr,total:tr.length};
      STATUS_LIST.forEach(s=>{entry[s]=tr.filter(r=>r.Status===s).length;});
      return entry;
    });

    return{total,open,closed,withdrawn,byStatus,byCompany,byTeam,byPriority,
           byMonth,byMonthStatus,byCompanyStatus,byTeamStatus,byPriorityStatus};
  },[filtered]);

  return{
    /* state */
    data,fileName,activeTab,exporting,dbLoadStatus,
    filterCompany,filterTeam,filterYear,filterStatus,
    /* computed */
    meta,filtered,stats,
    /* actions */
    loadFromDB,resetAll,setFilter,setActiveTab,setExporting,
    setFC,setFT,setFY,setFS,setData,setFileName,
  };
}

/* ══════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════ */
export default function SolmanDashboard({onDataChange, dataLoader}={}){
  const { user } = useAuth();  // ← Get current user
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const{
    data,fileName,activeTab,exporting,dbLoadStatus,
    filterCompany,filterTeam,filterYear,filterStatus,
    meta,filtered,stats,
    loadFromDB,resetAll,setFilter,setActiveTab:setAT,setExporting,
    setFC,setFT,setFY,setFS,setData,setFileName,
  }=useSolmanData(dataLoader);

  const[dbSync,setDbSync]=useState({status:null,message:""});

  // Jump-to-tab via URL param ?tab=xxx
  // Dulu `useEffect(…, [])` — hanya jalan saat mount, padahal halaman ini tidak
  // pernah di-unmount, sehingga deep link mati setelah kunjungan pertama.
  const NAV_IDS = ['overview','trends','teams','detail','pivot','aging','export'];
  useTabDeepLink('solman', (p) => NAV_IDS.includes(p), setAT);

  /* ── Ticket detail drawer (Overview + Teams) ── */
  const[sel,setSel]=useState(null);  // {team,status,monthLabel,tickets}
  const openTickets=(team,statusLabel,tickets)=>setSel({team,status:statusLabel,monthLabel:null,tickets});

  /* ── Notify parent whenever filtered or stats change ── */
  useEffect(()=>{
    if(typeof onDataChange==="function"){
      onDataChange({
        filtered,stats,data,
        filters:{company:filterCompany,team:filterTeam,year:filterYear,status:filterStatus},
      });
    }
  },[filtered,stats]);  // eslint-disable-line



  /* ── Export: embed data as JSON into a copy of this HTML ── */
  const handleExport=useCallback(async()=>{
    if(!data||exporting)return;
    setExporting(true);
    try{
      // Serialize rows — strip _date (not JSON-serializable), keep all other fields
      const exportRows=data.map(r=>{
        const{_date,...rest}=r;
        return rest;
      });
      const jsonStr=JSON.stringify(exportRows);

      // Get the current HTML source
      const currentHTML=document.documentElement.outerHTML;

      // Inject embedded data + boot script right before </body>
      // The exported HTML will call loadEmbedded() instead of showing upload screen
      const embedScript=`
<script id="__solman_embed__">
window.__SOLMAN_DATA__=${jsonStr};
window.__SOLMAN_FILE__=${JSON.stringify(fileName)};
<\/script>`;

      // Insert before </body>
      const exportHTML=currentHTML.replace('</body>',embedScript+'</body>');

      // Trigger download
      const blob=new Blob([exportHTML],{type:"text/html;charset=utf-8"});
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");
      const safeName=fileName.replace(/\.csv$/i,"").replace(/[^a-z0-9_\-]/gi,"_");
      a.href=url;
      a.download=`Solman_Dashboard_${safeName}_${new Date().toISOString().slice(0,10)}.html`;
      document.body.appendChild(a);
      a.click();
      setTimeout(()=>{URL.revokeObjectURL(url);document.body.removeChild(a);},1000);
    }finally{
      setExporting(false);
    }
  },[data,fileName,exporting]);


  const hasFilter=filterCompany!=="All"||filterTeam!=="All"||filterYear.length>0||filterStatus!=="All";
  const statusCfgOf=s=>STATUS_COLORS[s]||{dot:"#94A3B8",bg:"#F9FAFB",chart:"#94A3B8"};

  /* helper: Status summary cards row */
  const StatusSummaryRow=({label})=>(
    <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:4}}>
      <span style={{fontSize:12,color:T.textTertiary,alignSelf:"center",minWidth:60}}>{label}</span>
      {stats.byStatus.map(([s,cnt])=>{
        const cfg=statusCfgOf(s);
        return(
          <div key={s} style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:99,background:cfg.bg,border:`1px solid ${cfg.dot}22`}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:cfg.dot,flexShrink:0}}/>
            <span style={{fontSize:12,color:T.textSecondary}}>{STATUS_SHORT(s)}</span>
            <span style={{fontSize:12,fontWeight:700,color:cfg.dot}}>{cnt}</span>
          </div>
        );
      })}
    </div>
  );

  /* ── Section header divider ─── */
  const SectionLabel=({text})=>(
    <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0 0"}}>
      <div style={{flex:1,height:1,background:T.border}}/>
      <span style={{fontSize:11,color:T.textTertiary,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.07em",whiteSpace:"nowrap"}}>{text}</span>
      <div style={{flex:1,height:1,background:T.border}}/>
    </div>
  );

  return(
    <div style={{display:"flex",flexDirection:"column",minHeight:"100vh",background:T.bg,fontFamily:"system-ui,-apple-system,sans-serif"}}>

      <TicketDrawer sel={sel} onClose={()=>setSel(null)}/>

      <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column"}}>


      {/* ═══════════════════════════════════════════════════════════════
          HEADER - UPLOAD CSV + FILE INFO + KPI STATS
      ═══════════════════════════════════════════════════════════════ */}
      <div style={{background:"linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)",borderBottom:`1px solid rgb(var(--brand-500))`,padding:"20px 28px",display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:20}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:800,color:"#fff",margin:0,letterSpacing:"-0.5px"}}>{t('solm.title')}</h1>
          <p style={{fontSize:12,color:"#9CA3AF",margin:"4px 0 0"}}>{t('solm.subtitle')}</p>
          {data?.length>0&&<p style={{fontSize:11,color:"#D1D5DB",margin:"6px 0 0",display:"flex",alignItems:"center",gap:6}}><span>📊</span> <span style={{fontWeight:600}}>{fileName}</span> <span>·</span> <span>{data.length.toLocaleString()} {t('solm.records')}</span></p>}
        </div>
        <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
          {stats&&(<><div style={{width:1,height:24,background:"rgba(255,255,255,0.2)"}}/>
            {[{label:t('solm.total'),value:filtered.length,color:"#fff"},{label:t('solm.open'),value:stats.open||0,color:"#FCD34D"},{label:t('solm.closed'),value:stats.closed||0,color:"#6EE7B7"}].map(kpi=>(<div key={kpi.label} title={kpi.label===t('solm.open')?t('solm.nav.detail'):undefined} onClick={kpi.label===t('solm.open')?()=>{setFS("__OPEN__");setAT("detail");}:undefined} style={{textAlign:"right",cursor:kpi.label===t('solm.open')?"pointer":"default"}}><div style={{fontSize:10,color:"#D1D5DB",textTransform:"uppercase",letterSpacing:"1px",fontWeight:700}}>{kpi.label}</div><div style={{fontSize:16,fontWeight:800,color:kpi.color}}>{kpi.value.toLocaleString()}</div></div>))}</>)}
        </div>
      </div>

        {/* ── Top bar ── */}
        <div style={{padding:"16px 28px",background:T.card,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <h2 style={{fontSize:19,fontWeight:800,color:T.text,letterSpacing:"-0.5px",margin:0,lineHeight:1.2}}>{t(NAV.find(n=>n.id===activeTab)?.labelKey||'solm.nav.overview')}</h2>
            <p style={{fontSize:12,color:T.textTertiary,margin:0,marginTop:2}}>{t('solm.incidentTickets')}</p>
          </div>
          <div style={{display:"flex",gap:20}}>
            {[{l:t('solm.total'),v:filtered.length,c:T.blue},{l:t('solm.open'),v:stats?.open??0,c:T.amber},{l:t('solm.closed'),v:stats?.closed??0,c:T.green}].map(s=>(
              <div key={s.l} onClick={s.l===t('solm.open')?()=>{setFS("__OPEN__");setAT("detail");}:undefined} style={{textAlign:"right",cursor:s.l===t('solm.open')?"pointer":"default"}}>
                <div style={{fontSize:11,color:T.textTertiary}}>{s.l}</div>
                <div style={{fontSize:16,fontWeight:700,color:s.c}}>{s.v.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Filter bar ── */}
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",padding:"10px 28px",background:T.bg,borderBottom:`1px solid ${T.border}`}}>
          <i className="ti ti-adjustments-horizontal" style={{fontSize:15,color:T.textTertiary}}/>
          {/* Company */}
          <select value={filterCompany} onChange={e=>setFC(e.target.value)} style={{fontSize:13,padding:"6px 10px",borderRadius:8,border:`1px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer"}}>
            <option value="All">{t('solm.allCompanies')}</option>
            {meta.companies?.map(o=><option key={o} value={o}>{o}</option>)}
          </select>
          {/* Team = Impact */}
          <select value={filterTeam} onChange={e=>setFT(e.target.value)} style={{fontSize:13,padding:"6px 10px",borderRadius:8,border:`1px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer"}}>
            <option value="All">{t('solm.allTeams')}</option>
            {meta.teams?.map(o=><option key={o} value={o}>{o}</option>)}
          </select>
          {/* YEAR — BUTTONS WITH "ALL" */}
          <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:9,color:T.textTertiary,fontWeight:800,letterSpacing:"0.5px",textTransform:"uppercase",minWidth:45}}>{t('solm.year')}</span>
            <button onClick={()=>setFY([])} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:600,border:filterYear.length===0?"none":`1px solid ${T.border}`,cursor:"pointer",background:filterYear.length===0?T.blue:"#f8fafc",color:filterYear.length===0?"#fff":T.textTertiary}}>All</button>
            {meta.years?.map(y=>{const yearStr=String(y);const active=filterYear.includes(yearStr);return(<button key={y} onClick={(e)=>{if(e.ctrlKey||e.metaKey){setFY(prev=>prev.includes(yearStr)?prev.filter(x=>x!==yearStr):[...prev,yearStr]);}else{setFY(prev=>prev.length===1&&prev[0]===yearStr?[]:[yearStr]);}}} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:600,border:active?"none":`1px solid ${T.border}`,cursor:"pointer",background:active?T.blue:"#f8fafc",color:active?"#fff":T.textTertiary}}>{y}</button>);})}
          </div>
          {/* STATUS — BUTTONS WITH "ALL" */}
          <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:9,color:T.textTertiary,fontWeight:800,letterSpacing:"0.5px",textTransform:"uppercase",minWidth:65}}>{t('solm.status')}</span>
            <button onClick={()=>setFS("All")} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:600,border:filterStatus==="All"?"none":`1px solid ${T.border}`,cursor:"pointer",background:filterStatus==="All"?T.blue:"#f8fafc",color:filterStatus==="All"?"#fff":T.textTertiary}}>All</button>
            <button onClick={()=>setFS(filterStatus==="__OPEN__"?"All":"__OPEN__")} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:600,border:filterStatus==="__OPEN__"?"none":`1px solid ${T.amber}`,cursor:"pointer",background:filterStatus==="__OPEN__"?T.amber:"#f8fafc",color:filterStatus==="__OPEN__"?"#fff":T.amber}}>Open (all)</button>
            {[...STATUS_LIST,...[...new Set((data||[]).map(r=>r.Status))].filter(s=>s&&!STATUS_LIST.includes(s)).sort()].map(s=>{const active=filterStatus===s;const cfg=statusCfgOf(s);/*STATUS_EXTRA*/return(<button key={s} onClick={()=>setFS(active?"All":s)} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:600,border:active?"none":`1px solid ${cfg.dot}`,cursor:"pointer",background:active?cfg.dot:"#f8fafc",color:active?"#fff":cfg.dot}}>{STATUS_SHORT(s)}</button>);})}
          </div>
          {hasFilter&&(
            <button onClick={()=>{setFC("All");setFT("All");setFY([]);setFS("All");}} style={{fontSize:12,padding:"5px 12px",borderRadius:99,background:"#FEF2F2",color:T.red,border:`1px solid #FECACA`,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
              <i className="ti ti-x" style={{fontSize:13}}/> {t('solm.clear')}
            </button>
          )}
          <span style={{marginLeft:"auto",fontSize:13,color:T.textSecondary}}>
            {t('solm.showing')} <span style={{fontWeight:700,color:T.text}}>{filtered.length.toLocaleString()}</span> {t('solm.tickets')}
          </span>
        </div>


        {/* ── TAB BUTTONS ── */}
        {data?.length>0&&(
          <div style={{background:T.card,borderBottom:`1px solid ${T.border}`,paddingLeft:"28px",display:"flex",gap:0}}>
            {NAV.map(nav=>(
              <button key={nav.id} onClick={()=>setAT(nav.id)} style={{padding:"12px 20px",fontSize:13,fontWeight:700,border:"none",cursor:"pointer",background:"transparent",color:activeTab===nav.id?T.blue:T.textTertiary,borderBottom:activeTab===nav.id?`2px solid ${T.blue}`:"2px solid transparent",marginBottom:-1,transition:"all 0.2s"}}>
                {t(nav.labelKey)}
              </button>
            ))}
          </div>
        )}


        {/* ── Page content ── */}
        <div style={{flex:1,overflowY:"auto",padding:"24px 28px"}}>

          {/* ── EMPTY STATE ── */}
          {!data&&(
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",minHeight:400,textAlign:"center",gap:16}}>
              {dbLoadStatus==="loading"?(
                <>
                  <div style={{width:72,height:72,borderRadius:20,background:T.blueBg,border:`1px solid #BFDBFE`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <i className="ti ti-loader-2" style={{fontSize:34,color:T.blue,animation:"spin 1s linear infinite"}}/>
                  </div>
                  <div>
                    <h2 style={{fontSize:18,fontWeight:700,color:T.text,margin:"0 0 6px"}}>{t('solm.loading')}</h2>
                    <p style={{fontSize:13,color:T.textSecondary}}>{t('solm.loadingSub')}</p>
                  </div>
                </>
              ):dbLoadStatus==="error"?(
                <>
                  <div style={{width:72,height:72,borderRadius:20,background:"#FEF2F2",border:`1px solid #FECACA`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <i className="ti ti-database-x" style={{fontSize:34,color:T.red}}/>
                  </div>
                  <div>
                    <h2 style={{fontSize:18,fontWeight:700,color:T.text,margin:"0 0 6px"}}>{t('solm.cantLoad')}</h2>
                    <p style={{fontSize:13,color:T.textSecondary,maxWidth:320}}>{t('solm.cantLoadDesc')}</p>
                  </div>
                  <button onClick={loadFromDB} style={{padding:"8px 20px",borderRadius:8,background:T.blue,color:"#fff",border:"none",fontSize:13,cursor:"pointer",fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                    <i className="ti ti-refresh" style={{fontSize:14}}/> {t('solm.retry')}
                  </button>
                </>
              ):dbLoadStatus==="empty"?(
                <>
                  <div style={{width:72,height:72,borderRadius:20,background:T.amberBg,border:`1px solid #FDE68A`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <i className="ti ti-database" style={{fontSize:34,color:T.amber}}/>
                  </div>
                  <div>
                    <h2 style={{fontSize:18,fontWeight:700,color:T.text,margin:"0 0 6px"}}>{t('solm.dbEmpty')}</h2>
                    <p style={{fontSize:13,color:T.textSecondary,maxWidth:320}}>{t('solm.dbEmptyDesc')}</p>
                  </div>
                </>
              ):(
                <>
                  <div style={{width:72,height:72,borderRadius:20,background:T.blueBg,border:`1px solid #BFDBFE`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <i className="ti ti-file-spreadsheet" style={{fontSize:34,color:T.blue}}/>
                  </div>
                  <div>
                    <h2 style={{fontSize:20,fontWeight:800,color:T.text,margin:"0 0 8px",letterSpacing:"-0.4px"}}>{t('solm.noData')}</h2>
                    <p style={{fontSize:14,color:T.textSecondary,lineHeight:1.7,maxWidth:360}}>
                      {t('solm.noDataDesc')}
                    </p>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center"}}>
                    {[t('solm.chip1'),t('solm.chip2'),t('solm.chip3'),t('solm.chip4'),t('solm.chip5')].map(chip=>(
                      <span key={chip} style={{fontSize:12,padding:"4px 12px",borderRadius:99,background:T.card,color:T.textSecondary,border:`1px solid ${T.border}`}}>{chip}</span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══════ OVERVIEW ══════ */}
          {data&&activeTab==="overview"&&stats&&(
            <div style={{display:"flex",flexDirection:"column",gap:20}}>

              {/* KPI */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:14}}>
                <KpiCard label={t('solm.totalTickets')} value={stats.total}     icon={Ticket}        color={T.blue}   bg={T.blueBg}   onClick={()=>openTickets(t('solm.totalTickets'),"All statuses",filtered)}/>
                <KpiCard label={t('solm.open')}          value={stats.open}      icon={Clock}         color={T.amber}  bg={T.amberBg}  sub={`${Math.round((stats.open/stats.total)*100)}${t('solm.ofTotal')}`} onClick={()=>openTickets(t('solm.open'),"Open",filtered.filter(r=>OPEN_STATUSES.includes(r.Status)))}/>
                <KpiCard label={t('solm.closed')}        value={stats.closed}    icon={CheckCircle}   color={T.green}  bg={T.greenBg}  sub={`${Math.round((stats.closed/stats.total)*100)}${t('solm.ofTotal')}`} onClick={()=>openTickets(t('solm.closed'),"Closed",filtered.filter(r=>r.Status==="Closed"))}/>
                <KpiCard label={t('solm.withdrawn')}     value={stats.withdrawn} icon={XCircle}       color="#6B7280"  bg="#F9FAFB"    onClick={()=>openTickets(t('solm.withdrawn'),"Withdrawn",filtered.filter(r=>r.Status==="Withdrawn"))}/>
                <KpiCard label={t('solm.closeRate')}    value={`${Math.round((stats.closed/stats.total)*100)}%`} icon={Percent} color={T.teal} bg={T.tealBg}/>
              </div>

              {/* Status badge summary */}
              <Card>
                <StatusSummaryRow label="Status"/>
              </Card>

              {/* Status bars + Company */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                <Card title={t('solm.statusBreakdown')} subtitle={t('solm.statusBreakdownSub')}>
                  {stats.byStatus.map(([status,count])=>{
                    const cfg=statusCfgOf(status);
                    const pct=Math.round((count/stats.total)*100);
                    return(
                      <div key={status} onClick={count?()=>openTickets(t('solm.allTeams'),status,filtered.filter(r=>r.Status===status)):undefined} style={{marginBottom:14,cursor:count?"pointer":"default",borderRadius:8,padding:"4px 6px",margin:"0 -6px 10px",transition:"background 0.15s"}} onMouseEnter={count?e=>e.currentTarget.style.background=T.bg:undefined} onMouseLeave={count?e=>e.currentTarget.style.background="transparent":undefined}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,alignItems:"center"}}>
                          <div style={{display:"flex",alignItems:"center",gap:7}}>
                            <span style={{width:8,height:8,borderRadius:"50%",background:cfg.dot,display:"inline-block",flexShrink:0}}/>
                            <span style={{fontSize:13,color:T.textSecondary}}>{status}</span>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:11,color:T.textTertiary}}>{pct}%</span>
                            <span style={{fontSize:13,fontWeight:700,color:T.text,minWidth:28,textAlign:"right"}}>{count}</span>
                          </div>
                        </div>
                        <div style={{height:5,borderRadius:99,background:T.bg,overflow:"hidden",border:`1px solid ${T.border}`}}>
                          <div style={{height:"100%",width:`${pct}%`,background:cfg.chart,borderRadius:99,transition:"width 0.6s ease"}}/>
                        </div>
                      </div>
                    );
                  })}
                </Card>
                <Card title={t('solm.ticketsByCompany')} subtitle="Distribution across entities">
                  <ResponsiveContainer width="100%" height={Math.max(230, stats.byCompanyStatus.length*34+60)}>
                    <BarChart data={stats.byCompanyStatus} layout="vertical" margin={{left:0,right:40}}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={T.border}/>
                      <XAxis type="number" tick={{fontSize:11,fill:T.textTertiary}} axisLine={false} tickLine={false}/>
                      <YAxis dataKey="company" type="category" tick={{fontSize:13,fill:T.textSecondary}} width={36} axisLine={false} tickLine={false}/>
                      <Tooltip contentStyle={{fontSize:13,border:`1px solid ${T.border}`,borderRadius:10}} cursor={{fill:"rgba(37,99,235,0.04)"}}/>
                      <Legend iconSize={10} wrapperStyle={{fontSize:11,paddingTop:6}}/>
                      {STATUS_LIST.map((s,i)=>(
                        <Bar key={s} dataKey={s} name={STATUS_SHORT(s)} stackId="co" fill={STATUS_COLORS[s].chart}>
                          <LabelList dataKey="company" content={(p)=>{
                            const row=stats.byCompanyStatus.find(r=>r.company===p.value);
                            if(!row) return null;
                            const last=[...STATUS_LIST].reverse().find(st=>row[st]>0);
                            if(last!==s) return null;
                            return <text x={p.x+p.width+6} y={p.y+p.height/2} dy={4} fontSize={12} fill={T.textSecondary}>{row.total}</text>;
                          }}/>
                        </Bar>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>

              <SectionLabel text="Monthly breakdown by Status"/>

              {/* Month × Status stacked bar */}
              <Card title={t('solm.monthlyStacked')} subtitle={t('solm.monthlyStackedSub')}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stats.byMonthStatus} margin={{top:4,right:20,left:0,bottom:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false}/>
                    <XAxis dataKey="label" tick={{fontSize:11,fill:T.textTertiary}} interval={Math.max(0,Math.floor(stats.byMonthStatus.length/14))} angle={-35} textAnchor="end" height={52} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:11,fill:T.textTertiary}} axisLine={false} tickLine={false}/>
                    <Tooltip contentStyle={{fontSize:13,border:`1px solid ${T.border}`,borderRadius:10}}/>
                    <Legend iconSize={10} wrapperStyle={{fontSize:11,paddingTop:10}}/>
                    {STATUS_LIST.map(s=><Bar key={s} dataKey={s} name={STATUS_SHORT(s)} stackId="a" fill={STATUS_COLORS[s].chart}/>)}
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              {/* Month × Status detail table */}
              <MonthStatusTable data={stats.byMonthStatus} title={t('solm.monthStatusTable')} subtitle={t('solm.monthStatusSub')} filtered={filtered} openTickets={openTickets}/>

              <SectionLabel text="Monthly summary (Closed vs Open)"/>
              <MonthlyBreakdown filtered={filtered}/>

              <SectionLabel text="Priority distribution"/>
              <Card title={t('solm.priorityDist')} subtitle={t('solm.priorityDistSub')}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12}}>
                  {stats.byPriority.map(({name,value})=>{
                    const cfg=PRIORITY_CFG[name]||{color:T.textSecondary,bg:T.bg,label:name};
                    return(
                      <div key={name} style={{background:cfg.bg,borderRadius:12,padding:"18px 20px",borderLeft:`4px solid ${cfg.color}`}}>
                        <div style={{fontSize:11,color:cfg.color,fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>{cfg.label}</div>
                        <div style={{fontSize:32,fontWeight:800,color:T.text,lineHeight:1,letterSpacing:"-1px"}}>{value}</div>
                        <div style={{fontSize:12,color:T.textSecondary,marginTop:7}}>{Math.round((value/stats.total)*100)}% of total</div>
                      </div>
                    );
                  })}
                </div>
              </Card>

            </div>
          )}

          {/* ══════ TRENDS ══════ */}
          {data&&activeTab==="trends"&&stats&&(
            <div style={{display:"flex",flexDirection:"column",gap:20}}>

              {/* Area chart total/closed/open */}
              <Card title={t('solm.monthlyVolume')} subtitle={t('solm.monthlyVolumeSub')}>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={stats.byMonth} margin={{top:4,right:20,left:0,bottom:0}}>
                    <defs>
                      <linearGradient id="gT" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.blue} stopOpacity={0.18}/><stop offset="100%" stopColor={T.blue} stopOpacity={0}/></linearGradient>
                      <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.green} stopOpacity={0.14}/><stop offset="100%" stopColor={T.green} stopOpacity={0}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false}/>
                    <XAxis dataKey="label" tick={{fontSize:11,fill:T.textTertiary}} interval={Math.max(0,Math.floor(stats.byMonth.length/14))} angle={-35} textAnchor="end" height={52} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:11,fill:T.textTertiary}} axisLine={false} tickLine={false}/>
                    <Tooltip contentStyle={{fontSize:13,border:`1px solid ${T.border}`,borderRadius:10}}/>
                    <Legend iconSize={10} wrapperStyle={{fontSize:12,paddingTop:12}}/>
                    <Area type="monotone" dataKey="total"  name="Total"  stroke={T.blue}  strokeWidth={2.5} fill="url(#gT)"  dot={false}/>
                    <Area type="monotone" dataKey="closed" name="Closed" stroke={T.green} strokeWidth={2}   fill="url(#gC)" dot={false}/>
                    <Line type="monotone" dataKey="open"   name="Open"   stroke={T.amber} strokeWidth={1.5} dot={false} strokeDasharray="5 3"/>
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <SectionLabel text="Monthly breakdown by Status"/>

              {/* Stacked bar by status */}
              <Card title={t('solm.monthlyStacked2')} subtitle={t('solm.monthlyStacked2Sub')}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.byMonthStatus} margin={{top:4,right:20,left:0,bottom:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false}/>
                    <XAxis dataKey="label" tick={{fontSize:11,fill:T.textTertiary}} interval={Math.max(0,Math.floor(stats.byMonthStatus.length/14))} angle={-35} textAnchor="end" height={52} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:11,fill:T.textTertiary}} axisLine={false} tickLine={false}/>
                    <Tooltip contentStyle={{fontSize:13,border:`1px solid ${T.border}`,borderRadius:10}}/>
                    <Legend iconSize={10} wrapperStyle={{fontSize:11,paddingTop:10}}/>
                    {STATUS_LIST.map(s=><Bar key={s} dataKey={s} name={STATUS_SHORT(s)} stackId="a" fill={STATUS_COLORS[s].chart}/>)}
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              {/* Month × Status detail table */}
              <MonthStatusTable data={stats.byMonthStatus} title={t('solm.monthStatusTable')} subtitle={t('solm.monthStatusSub')} filtered={filtered} openTickets={openTickets}/>

              <SectionLabel text="Monthly summary"/>
              <MonthlyBreakdown filtered={filtered}/>

            </div>
          )}

          {/* ══════ TEAMS ══════ */}
          {data&&activeTab==="teams"&&stats&&(
            <div style={{display:"flex",flexDirection:"column",gap:20}}>

              {/* Team volume bar */}
              <Card title={t('solm.ticketVolPerTeam')} subtitle="From Impact field">
                <ResponsiveContainer width="100%" height={Math.max(260,stats.byTeam.length*46)}>
                  <BarChart data={stats.byTeam} layout="vertical" margin={{left:10,right:56}}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={T.border}/>
                    <XAxis type="number" tick={{fontSize:11,fill:T.textTertiary}} axisLine={false} tickLine={false}/>
                    <YAxis dataKey="name" type="category" tick={{fontSize:13,fill:T.textSecondary}} width={130} axisLine={false} tickLine={false}/>
                    <Tooltip contentStyle={{fontSize:13,border:`1px solid ${T.border}`,borderRadius:10}} cursor={{fill:"rgba(37,99,235,0.04)"}}/>
                    <Bar dataKey="value" name="Tickets" fill={T.blue} radius={[0,7,7,0]} label={{position:"right",fontSize:12,fill:T.textSecondary}}/>
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <SectionLabel text="Team × Status breakdown"/>

              {/* Team × Status matrix */}
              <Card title={t('solm.teamStatusMatrix')} subtitle={t('solm.teamStatusMatrixSub')}>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{background:T.bg}}>
                        <th style={{padding:"11px 16px",textAlign:"left",fontWeight:700,color:T.textSecondary,borderBottom:`1px solid ${T.border}`,fontSize:11,textTransform:"uppercase",letterSpacing:"0.06em",whiteSpace:"nowrap",position:"sticky",left:0,background:T.bg,zIndex:2}}>{t('solm.teamImpact')}</th>
                        {STATUS_LIST.map(s=>(
                          <th key={s} style={{padding:"11px 10px",textAlign:"center",fontWeight:600,color:STATUS_COLORS[s].dot,borderBottom:`1px solid ${T.border}`,whiteSpace:"nowrap",fontSize:11,textTransform:"uppercase",letterSpacing:"0.04em"}}>
                            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                              <span style={{width:8,height:8,borderRadius:"50%",background:STATUS_COLORS[s].dot}}/>
                              {STATUS_SHORT(s)}
                            </div>
                          </th>
                        ))}
                        <th style={{padding:"11px 14px",textAlign:"center",fontWeight:700,color:T.blue,borderBottom:`1px solid ${T.border}`,fontSize:12,background:T.blueBg}}>{t('solm.total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.byTeamStatus.map((row,i)=>(
                        <tr key={row.team} style={{background:i%2===0?T.card:T.bg}}>
                          <td style={{padding:"10px 16px",fontWeight:600,borderBottom:`1px solid ${T.border}`,color:T.text,whiteSpace:"nowrap",position:"sticky",left:0,background:i%2===0?T.card:T.bg,zIndex:1}}>{row.team}</td>
                          {STATUS_LIST.map(s=>(
                            <td key={s} onClick={row[s]?()=>openTickets(row.team,s,filtered.filter(r=>(r.Impact||"Unknown")===row.team&&r.Status===s)):undefined} style={{padding:"10px",textAlign:"center",borderBottom:`1px solid ${T.border}`,cursor:row[s]?"pointer":"default"}}>
                              {row[s]?<StatusBadge status={s} count={row[s]}/>:<span style={{color:T.border}}>—</span>}
                            </td>
                          ))}
                          <td onClick={row._total?()=>openTickets(row.team,"All statuses",filtered.filter(r=>(r.Impact||"Unknown")===row.team)):undefined} style={{padding:"10px 14px",textAlign:"center",fontWeight:800,color:T.blue,background:T.blueBg,borderBottom:`1px solid ${T.border}`,fontSize:14,cursor:row._total?"pointer":"default"}}>{row._total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <SectionLabel text="Monthly breakdown by Status"/>

              {/* Month × Status for current team filter */}
              <MonthStatusTable data={stats.byMonthStatus} title={t('solm.monthStatusFilter')} subtitle={t('solm.monthStatusFilterSub')} filtered={filtered} openTickets={openTickets}/>

              <SectionLabel text="Monthly summary"/>
              <MonthlyBreakdown filtered={filtered}/>

            </div>
          )}

          {/* ══════ DETAIL TIKET ══════ */}
          {data&&activeTab==="detail"&&(
            <div style={{display:"flex",flexDirection:"column",gap:20}}>
              <SectionLabel text={t('solm.det.sub')}/>
              <TicketDetail filtered={filtered} filters={{company:filterCompany,team:filterTeam,year:filterYear,status:filterStatus}}/>
            </div>
          )}

          {/* ══════ PIVOT ══════ */}
          {data&&activeTab==="pivot"&&stats&&(
            <div style={{display:"flex",flexDirection:"column",gap:20}}>

          <ImpactTeamMap />
              <SectionLabel text="Excel-style pivot — Impact × Status × Month"/>

              {/* ← NEW: Excel-style collapsible pivot */}
              <ExcelPivot filtered={filtered}/>

              <SectionLabel text="Status summary for current filter"/>

              {/* Status summary pills */}
              <Card>
                <StatusSummaryRow label="Status"/>
                <div style={{marginTop:12}}>
                  <ResponsiveContainer width="100%" height={60}>
                    <BarChart data={stats.byStatus.map(([s,v])=>({name:STATUS_SHORT(s),value:v,fill:statusCfgOf(s).chart}))} layout="vertical" margin={{left:0,right:30,top:0,bottom:0}}>
                      <XAxis type="number" hide/>
                      <YAxis dataKey="name" type="category" tick={{fontSize:11,fill:T.textSecondary}} width={100} axisLine={false} tickLine={false}/>
                      <Tooltip contentStyle={{fontSize:12,border:`1px solid ${T.border}`,borderRadius:8}} cursor={{fill:"rgba(0,0,0,0.03)"}}/>
                      <Bar dataKey="value" radius={[0,4,4,0]} label={{position:"right",fontSize:11,fill:T.textSecondary}}>
                        {stats.byStatus.map(([s],i)=><Cell key={i} fill={statusCfgOf(s).chart}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <SectionLabel text="Month × Status detail table"/>
              <MonthStatusTable data={stats.byMonthStatus} title={t('solm.monthStatusDetail')} subtitle={t('solm.monthStatusDetailSub')} filtered={filtered} openTickets={openTickets}/>

              <SectionLabel text="Interactive pivot"/>
              <PivotTable filtered={filtered}/>

            </div>
          )}

          {activeTab === "aging" && <SolmanStatusAging />}

          {activeTab === "report" && (
            <SolmanMonthlyReport
              exportPanel={<ExportPanel data={data} meta={meta} T={T} t={t}
                                        defaultCompany={filterCompany} defaultYear={filterYear}/>} />
          )}

        </div>
      </div>
    </div>
  );
}

/* ── Named exports for parent integration ──────
 *
 * 1. Hook only (parent manages state):
 *    import { useSolmanData } from "./SolmanDashboard";
 *    const { filtered, stats, setFilter } = useSolmanData();
 *
 * 2. Component with callback:
 *    import SolmanDashboard from "./SolmanDashboard";
 *    <SolmanDashboard onDataChange={({filtered,stats,data,filters})=>{
 *      console.log("filtered count:", filtered.length);
 *      console.log("total tickets:", stats?.total);
 *    }}/>
 *
 * 3. Both together (full control):
 *    const solman = useSolmanData();
 *    // solman.filtered, solman.stats, solman.setFilter({year:"2026"})
 *    // solman.meta.companies, solman.meta.teams ...
 * ─────────────────────────────────────────── */
export { useSolmanData };
